import { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Textarea,
  Text,
  VStack,
  HStack,
  Button,
  ButtonGroup,
  Select,
  SimpleGrid,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { WebAPIContext } from "../../contexts/WebAPIContext";
import { formatNumber, hasDecimal, getLocalCurrencyId, formatYearMonthByLocale, formatDateByLocale } from "../../utils/utils";
import { getPricesByPartNoMonth, getQuantitiesByPartNoMonth } from "../../utils/calculation";
import { convertCurrency } from "../../utils/CurrencyConverter";
import { convertCurrencyByMonth } from "../../utils/reviewCalculation";
import { CurrencyNameJPY, WorkflowId } from "../../contexts/constants";
import { ConfirmorInfo, Currency, PagePermissions, PartNo } from "../../contexts/types";
import Decimal from "decimal.js";
import { useAppSelector } from "../../store/hooks";
import { getConfirmor, updateConfirm } from "../../utils/workflow";
import ConfirmorList from "./ConfirmorList";
import HelpIcon from "./HelpIcon";
import * as XLSX from 'xlsx';
import { ProjectContext, getPermissions } from "./InvestmentScreeningDetail";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, annotationPlugin);

interface ReviewSalesProps {
  projectId: string;
  reviewDate?: string | null;
  monthlyRateMap?: Map<string, Currency[]>;
  isSimpleMode?: boolean;
  projectEndDate?: string | null;
}

type CompareMode = "previous" | "originalPlan";

// 比較モード別の色設定
const compareColors = {
  previous: { bg: "rgba(66, 153, 225, 0.7)", border: "rgba(66, 153, 225, 1)" },
  originalPlan: { bg: "rgba(72, 187, 120, 0.7)", border: "rgba(72, 187, 120, 1)" },
};

// チャートセクション型
interface ChartSectionData {
  id: string;
  title: string;
  unit: string;
  summaryType: "total" | "average";
  data: {
    originalPlan: number[];
    previous: number[];
    current: number[];
  };
}

// テキスト入力項目（販売期間は専用セクションに分離）
const TEXT_ITEM_IDS = ["competitor", "sales_expansion"] as const;

// 補足視点
const SUPPLEMENTARY_ITEM_IDS = [
  "competitive_advantage",
  "customer_satisfaction",
  "marketing",
  "qualitative_change",
] as const;

// 振り返りデータの型
interface ReviewComparisonData {
  originalPlan: number[];
  previous: number[];
  current: number[];
}

/**
 * P&Lデータのラベル配列を、現在のEOL予定日（projectEndDate）に合わせて延長または短縮する。
 * BasicInfoでEOL予定日が変更された場合に、振り返り画面のチャート期間を正しく反映するために使用。
 */
const adjustLabelsToEOL = (rawLabels: string[], projectEndDate: string | null | undefined): string[] => {
  if (!projectEndDate || rawLabels.length === 0) return rawLabels;
  const parts = projectEndDate.split('-');
  const eolYear = parseInt(parts[0]);
  const eolMonth = parseInt(parts[1]);

  // EOL予定日の属する年度末（4月始まり → 翌3月）を計算
  // 例: 2030/5 → FY2030 → 年度末 2031/3
  //     2029/8 → FY2029 → 年度末 2030/3
  const fiscalYear = eolMonth >= 4 ? eolYear : eolYear - 1;
  const targetYear = fiscalYear + 1;
  const targetMonth = 3;
  const targetYM = targetYear * 100 + targetMonth;

  const toYM = (label: string): number => {
    const lp = label.split('/');
    return parseInt(lp[0]) * 100 + parseInt(lp[1]);
  };

  const lastYM = toYM(rawLabels[rawLabels.length - 1]);

  if (targetYM < lastYM) {
    // 年度末が末尾より前 → 年度末以降を切り捨て
    return rawLabels.filter(l => toYM(l) <= targetYM);
  }
  if (targetYM === lastYM) return rawLabels;

  // 年度末が末尾より後 → 月単位で延長（最大120ヶ月）
  const extended = [...rawLabels];
  let curYear = parseInt(rawLabels[rawLabels.length - 1].split('/')[0]);
  let curMonth = parseInt(rawLabels[rawLabels.length - 1].split('/')[1]);
  while (extended.length <= rawLabels.length + 120) {
    curMonth++;
    if (curMonth > 12) { curMonth = 1; curYear++; }
    extended.push(`${curYear}/${curMonth}`);
    if (curYear === targetYear && curMonth === targetMonth) break;
  }
  return extended;
};

/**
 * 数値配列をターゲット長に合わせて延長（末尾を0埋め）または短縮（スライス）する。
 */
const extendOrTruncate = (arr: number[], targetLength: number): number[] => {
  if (arr.length === targetLength) return arr;
  if (arr.length > targetLength) return arr.slice(0, targetLength);
  return [...arr, ...new Array(targetLength - arr.length).fill(0)];
};

// Y軸の最大値を両モードで統一
const calcUnifiedMax = (data: { originalPlan: number[]; previous: number[]; current: number[] }, startIdx: number, endIdx: number): number => {
  const sliced = [
    ...data.originalPlan.slice(startIdx, endIdx),
    ...data.previous.slice(startIdx, endIdx),
    ...data.current.slice(startIdx, endIdx),
  ];
  const max = Math.max(...sliced);
  return Math.ceil(max * 1.1);
};

// ① 実績ベースの計画比（累計）
const calcActualVsPlan = (original: number[], curr: number[], boundaryIdx: number) => {
  const planValue = original.slice(0, boundaryIdx).reduce((a, b) => a + b, 0);
  const actualValue = curr.slice(0, boundaryIdx).reduce((a, b) => a + b, 0);
  const rate = planValue !== 0 ? ((actualValue - planValue) / Math.abs(planValue)) * 100 : 0;
  return { planValue, actualValue, rate };
};

// ②-1 前回予測の精度（累計）
const calcForecastAccuracy = (prev: number[], curr: number[], prevIdx: number, boundaryIdx: number) => {
  const prevEstimate = prev.slice(prevIdx, boundaryIdx).reduce((a, b) => a + b, 0);
  const currActual = curr.slice(prevIdx, boundaryIdx).reduce((a, b) => a + b, 0);
  const rate = prevEstimate !== 0 ? ((currActual - prevEstimate) / Math.abs(prevEstimate)) * 100 : 0;
  return { prevEstimate, currActual, rate };
};

// ③ 軌道修正後の計画比（累計）
const calcFinalOutlook = (original: number[], curr: number[]) => {
  const planTotal = original.reduce((a, b) => a + b, 0);
  const outlookTotal = curr.reduce((a, b) => a + b, 0);
  const rate = planTotal !== 0 ? ((outlookTotal - planTotal) / Math.abs(planTotal)) * 100 : 0;
  return { planTotal, outlookTotal, rate };
};

// 平均版
const calcActualVsPlanAvg = (original: number[], curr: number[], boundaryIdx: number) => {
  const planSlice = original.slice(0, boundaryIdx);
  const actualSlice = curr.slice(0, boundaryIdx);
  const planAvg = planSlice.length > 0 ? planSlice.reduce((a, b) => a + b, 0) / planSlice.length : 0;
  const actualAvg = actualSlice.length > 0 ? actualSlice.reduce((a, b) => a + b, 0) / actualSlice.length : 0;
  const rate = planAvg !== 0 ? ((actualAvg - planAvg) / Math.abs(planAvg)) * 100 : 0;
  return { planValue: planAvg, actualValue: actualAvg, rate };
};

const calcForecastAccuracyAvg = (prev: number[], curr: number[], prevIdx: number, boundaryIdx: number) => {
  const prevSlice = prev.slice(prevIdx, boundaryIdx);
  const currSlice = curr.slice(prevIdx, boundaryIdx);
  const prevAvg = prevSlice.length > 0 ? prevSlice.reduce((a, b) => a + b, 0) / prevSlice.length : 0;
  const currAvg = currSlice.length > 0 ? currSlice.reduce((a, b) => a + b, 0) / currSlice.length : 0;
  const rate = prevAvg !== 0 ? ((currAvg - prevAvg) / Math.abs(prevAvg)) * 100 : 0;
  return { prevEstimate: prevAvg, currActual: currAvg, rate };
};

const calcFinalOutlookAvg = (original: number[], curr: number[]) => {
  const planAvg = original.length > 0 ? original.reduce((a, b) => a + b, 0) / original.length : 0;
  const outlookAvg = curr.length > 0 ? curr.reduce((a, b) => a + b, 0) / curr.length : 0;
  const rate = planAvg !== 0 ? ((outlookAvg - planAvg) / Math.abs(planAvg)) * 100 : 0;
  return { planTotal: planAvg, outlookTotal: outlookAvg, rate };
};

const getAutoColWidths = (data: (string | number)[][]): XLSX.ColInfo[] => {
  const colWidths: number[] = [];
  for (const row of data) {
    row.forEach((cell, i) => {
      const cellValue = String(cell ?? '');
      const cellLength = [...cellValue].reduce(
        (sum, ch) => sum + (ch.charCodeAt(0) > 255 ? 2 : 1),
        0
      );
      colWidths[i] = Math.max(colWidths[i] || 10, cellLength);
    });
  }
  return colWidths.map(w => ({ wch: w + 2 }));
};

interface Confirmor {
  reviewSales?: {
    companyDivision?: ConfirmorInfo[];
    salesDivision?: ConfirmorInfo[];
  };
}

// 内部データ用のスケジュール型定義
interface NormalizedScheduleItem {
  id: string;
  name: string;
  date: string;
}

interface NormalizedFacilityItem {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

const ReviewSales: React.FC<ReviewSalesProps> = ({ projectId, reviewDate: reviewDateProp, monthlyRateMap, isSimpleMode = false, projectEndDate: projectEndDateProp }) => {
  const { t } = useTranslation();
  const WebAPI = useContext(WebAPIContext);
  const toast = useToast();
  const TITLE = t("features.InvestmentScreening.Review.ReviewSales.title");

  /** ログインユーザー：従業員ID */
  const employeeId = useAppSelector((state) => state.employee.employeeId);
  /** ログインユーザー：部署ID */
  const divisionId = useAppSelector((state) => state.employee.divisionId);
  /** ユーザー権限 */
  const projectContext = useContext(ProjectContext);
  const userPermissions = projectContext?.userPermissions ?? [];

  /** 権限 */
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();
  /** 起案者部署ID（確認ボタン活性判定用） */
  const [registrantDivisionId, setRegistrantDivisionId] = useState<string | null>(null);
  /** 確認担当者 */
  const [confirmor, setConfirmor] = useState<Confirmor | null>(null);
  /** 確認ボタン横幅 */
  const [buttonWidths, setButtonWidths] = useState<Record<string, number>>({});
  /** 確認ボタン下担当者名 */
  const [names, setNames] = useState<Record<string, ConfirmorInfo[]>>({
    companyDivision: [],
    salesDivision: [],
  });
  /** 確認元部署 */
  const [confirmSource, setConfirmSource] = useState<string>("");
  /** 保存中フラグ */
  const [isSaving, setIsSaving] = useState(false);
  /** 確認ボタン ref */
  const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
    companyDivision: useRef<HTMLButtonElement>(null),
    salesDivision: useRef<HTMLButtonElement>(null),
  };
  const { isOpen: isOpenConfirmModal, onOpen: onOpenConfirmModal, onClose: onCloseConfirmModal } = useDisclosure();
  const [compareMode, setCompareMode] = useState<CompareMode>("previous");
  const [monthLabels, setMonthLabels] = useState<string[]>([]);
  const [chartSections, setChartSections] = useState<ChartSectionData[]>([]);
  const [textValues, setTextValues] = useState<Record<string, string>>({});
  const [supplementaryValues, setSupplementaryValues] = useState<Record<string, string>>({});
  const [comments, setComments] = useState<Record<string, string>>({});

  // 通貨関連
  const [currencyName, setCurrencyName] = useState<string>(CurrencyNameJPY);
  const [localCurrencyName, setLocalCurrencyName] = useState<string>(CurrencyNameJPY);
  // ローカル通貨版とJPY版のデータを保持
  const [chartSectionsLocal, setChartSectionsLocal] = useState<ChartSectionData[]>([]);
  const [chartSectionsJPY, setChartSectionsJPY] = useState<ChartSectionData[]>([]);

  // PartNo別グラフ切り替え
  const [salesPartNos, setSalesPartNos] = useState<string[]>([]);
  // PartNoごとのdisplayPartNo解決マップ（No.435対応、画面表示・Excel出力・PdfReviewContent受け渡し用）
  const [displayPartNoMap, setDisplayPartNoMap] = useState<Record<string, string | null>>({});
  const [priceDataByPartNo, setPriceDataByPartNo] = useState<Record<string, ReviewComparisonData>>({});
  const [priceDataByPartNoJPY, setPriceDataByPartNoJPY] = useState<Record<string, ReviewComparisonData>>({});
  const [qtyDataByPartNo, setQtyDataByPartNo] = useState<Record<string, ReviewComparisonData>>({});
  const [selectedPartNoPriceGraph, setSelectedPartNoPriceGraph] = useState<string>("");
  const [selectedPartNoQtyGraph, setSelectedPartNoQtyGraph] = useState<string>("all");

  // 期間選択
  const [periodStart, setPeriodStart] = useState<number>(0);
  const [periodEnd, setPeriodEnd] = useState<number>(0);
  const [milestones, setMilestones] = useState<{ label: string; idx: number; isCurrent?: boolean }[]>([]);

  // 振り返り境界（Phase2でスケジュールテーブルから取得）
  const [reviewBoundaryIndex, setReviewBoundaryIndex] = useState<number>(18);
  const [prevReviewIndex, setPrevReviewIndex] = useState<number>(12);
  // 振り返り実データの有無
  const [hasReviewData, setHasReviewData] = useState<boolean>(false);
  const [reviewDataList, setReviewDataList] = useState<any[]>([]);

  // シェアデータ（申請時・前回・今回）
  const [sharePlan, setSharePlan] = useState<{ sharePercentage: number | null; shareOfCompetitorAndCompany: string | null } | null>(null);
  const [sharePrevious, setSharePrevious] = useState<{ sharePercentage: number | null; shareOfCompetitorAndCompany: string | null } | null>(null);
  const [shareReview, setShareReview] = useState<{ sharePercentage: number | null; shareOfCompetitorAndCompany: string | null } | null>(null);

  // 前回振り返り日（初回判定・ラベル表示用）
  const [prevReviewDateState, setPrevReviewDateState] = useState<string | null>(null);

  // 顧客スケジュール（マイルストーン対比用）
  const [planScheduleItems, setPlanScheduleItems] = useState<NormalizedScheduleItem[]>([]);
  const [prevScheduleItems, setPrevScheduleItems] = useState<NormalizedScheduleItem[]>([]);
  const [reviewScheduleItems, setReviewScheduleItems] = useState<NormalizedScheduleItem[]>([]);
  const [planProjectPeriod, setPlanProjectPeriod] = useState<{ start: string; end: string } | null>(null);
  // 設備投資スケジュール（遅延原因参照用）
  const [planFacilityItems, setPlanFacilityItems] = useState<NormalizedFacilityItem[]>([]);
  const [prevFacilityItems, setPrevFacilityItems] = useState<NormalizedFacilityItem[]>([]);
  const [reviewFacilityItems, setReviewFacilityItems] = useState<NormalizedFacilityItem[]>([]);

  useEffect(() => {
    const perms = getPermissions(userPermissions, WorkflowId.ReviewSales, undefined, employeeId, divisionId, registrantDivisionId ?? undefined);
    setPagePermissions(perms);
  }, [userPermissions, employeeId, divisionId, registrantDivisionId]);

  useEffect(() => {
    const widths: Record<string, number> = Object.keys(refs).reduce((acc, key) => {
      acc[key] = refs[key]?.current?.offsetWidth || 0;
      return acc;
    }, {} as Record<string, number>);
    setButtonWidths(widths);
  }, []);

  useEffect(() => {
    if (!projectId) return;
    const fetchConfirmorData = async () => {
      try {
        const data = await getConfirmor(WebAPI, projectId, reviewDateProp);
        setConfirmor(data);
      } catch { /* スルー */ }
    };
    fetchConfirmorData();
  }, [projectId, reviewDateProp]);

  useEffect(() => {
    if (confirmor?.reviewSales) {
      setNames({
        companyDivision: confirmor.reviewSales.companyDivision ?? [],
        salesDivision: confirmor.reviewSales.salesDivision ?? [],
      });
    }
  }, [confirmor]);

  useEffect(() => {
    if (!projectId) return;
    fetchData();
  }, [projectId, reviewDateProp, projectEndDateProp]);

  const fetchData = async () => {
    // プロジェクト切り替え時に前プロジェクトのデータが残らないよう初期化
    setPlanScheduleItems([]);
    setPrevScheduleItems([]);
    setReviewScheduleItems([]);
    setPlanProjectPeriod(null);
    setPlanFacilityItems([]);
    setPrevFacilityItems([]);
    setReviewFacilityItems([]);
    setSharePrevious(null);
    setPrevReviewDateState(null);
    try {
      let prevReviewDate: string | null = null;
      let prevReviewPartNos: any[] = [];
      let prevReviewProjectData: any = null;

      // 保存済みの sales_review を復元
      if (reviewDateProp) {
        try {
          const listResp = await (WebAPI as any).request({
            query: `
              query getReviewListForSales($projectId: String!) {
                getReviewListByProjectId(projectId: $projectId) {
                  reviewDate
                  salesReview
                }
              }
            `,
            variables: { projectId }
          });
          const reviewList: { reviewDate: string; salesReview?: string }[] =
            listResp?.data?.getReviewListByProjectId || [];
          // 前回振り返り日を特定（現在日より前の最新レビュー日を取得。現在日がリストに存在しない場合も考慮）
          const sortedReviews = [...reviewList].sort((a, b) => a.reviewDate.localeCompare(b.reviewDate));
          const prevDates = sortedReviews.filter(r => r.reviewDate < reviewDateProp);
          if (prevDates.length > 0) {
            prevReviewDate = prevDates[prevDates.length - 1].reviewDate;
            setPrevReviewDateState(prevReviewDate);
          }
          const current = reviewList.find(r => r.reviewDate === reviewDateProp);
          if (current?.salesReview) {
            try {
              const saved = JSON.parse(current.salesReview);
              if (saved.textValues) setTextValues(saved.textValues);
              if (saved.comments) setComments(saved.comments);
              if (saved.supplementaryValues) setSupplementaryValues(saved.supplementaryValues);
            } catch { /* パース失敗は無視 */ }
          }
        } catch { /* 失敗しても続行 */ }
      }

      // 振り返りデータの取得を試行
      let reviewData: any[] = [];
      let reviewPartNos: any[] = [];

      if (reviewDateProp) {
        try {
          const reviewProjResponse = await (WebAPI as any).request({
            query: `
              query getProjectForReview($projectId: String, $useRDSData: Boolean, $reviewDate: String) {
                getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
                  projectId
                  originalProfitAndLossByMonth
                  displayOriginalProfitAndLossByMonth
                  approvalStatus
                }
              }
            `,
            variables: { projectId, useRDSData: true, reviewDate: reviewDateProp },
          });
          const reviewProject = reviewProjResponse?.data?.getProjectById;
          if (reviewProject) {
            reviewData = [{ ...reviewProject, reviewDate: reviewDateProp }];
          }

          const reviewPartNoResponse = await (WebAPI as any).request({
            query: `
              query getPartNoForReview($projectId: String, $reviewDate: String) {
                getPartNo(projectId: $projectId, reviewDate: $reviewDate) {
                  partNo
                  displayPartNo
                  quantityAndPrice
                  moldSalesByPartNo
                  developmentSalesByPartNo
                }
              }
            `,
            variables: { projectId, reviewDate: reviewDateProp },
          });
          reviewPartNos = reviewPartNoResponse?.data?.getPartNo || [];

          // PartNoごとのdisplayPartNo解決マップを構築（No.435対応）
          // state反映は非同期のため、本関数内で以降このデータを使う場合はlocalDisplayPartNoMapを直接参照する
          const localDisplayPartNoMap: Record<string, string | null> = Object.fromEntries(
            reviewPartNos.map((p: PartNo) => [p.partNo, p.displayPartNo || null])
          );
          setDisplayPartNoMap(localDisplayPartNoMap);

          // 振り返りプロジェクトデータからプロジェクトレベルの売上も取得
          const reviewSalesResponse = await (WebAPI as any).request({
            query: `
              query getReviewProjectSales($projectId: String, $useRDSData: Boolean, $reviewDate: String) {
                getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
                  projectId
                  moldSalesByProject
                  developmentSalesByProject
                  equipmentSalesByProject
                  pricingAgreement
                  customerSchedule
                  equipmentInstallationSchedule
                }
              }
            `,
            variables: { projectId, useRDSData: true, reviewDate: reviewDateProp },
          });
          const reviewSalesProject = reviewSalesResponse?.data?.getProjectById;
          if (reviewSalesProject) {
            reviewData = [{ ...reviewSalesProject, reviewDate: reviewDateProp }];
            // シェアデータ（振り返り）を抽出
            if (reviewSalesProject.pricingAgreement) {
              try {
                const pa = JSON.parse(reviewSalesProject.pricingAgreement);
                setShareReview({
                  sharePercentage: pa?.competitor?.sharePercentage ?? null,
                  shareOfCompetitorAndCompany: pa?.competitor?.shareOfCompetitorAndCompany ?? null,
                });
              } catch { /* skip */ }
            }
            // 振り返りスケジュール（マイルストーン対比用）
            if (reviewSalesProject.customerSchedule) {
              try {
                const parsed = JSON.parse(reviewSalesProject.customerSchedule);
                const normalized = parsed
                  .filter((item: any) => item.name && item.date)
                  .map((item: any) => ({
                    ...item,
                    id: String(item.id ?? "")
                  }));
                setReviewScheduleItems(normalized);
              } catch { /* skip */ }
            }
            // 振り返り・設備投資スケジュール（遅延原因参照用）
            if (reviewSalesProject.equipmentInstallationSchedule) {
              try {
                const parsed = JSON.parse(reviewSalesProject.equipmentInstallationSchedule);
                const normalized = parsed
                  .filter((item: any) => item.name && item.startDate && item.endDate)
                  .map((item: any) => ({
                    ...item,
                    id: String(item.id ?? "")
                  }));
                setReviewFacilityItems(normalized);
              } catch { /* skip */ }
            }
          }

          // 前回振り返りデータを取得（前回値の表示用）
          if (prevReviewDate) {
            const [prevPartNoResp, prevProjResp] = await Promise.all([
              (WebAPI as any).request({
                query: `
                  query getPrevPartNo($projectId: String, $reviewDate: String) {
                    getPartNo(projectId: $projectId, reviewDate: $reviewDate) {
                      partNo
                      displayPartNo
                      quantityAndPrice
                      moldSalesByPartNo
                      developmentSalesByPartNo
                    }
                  }
                `,
                variables: { projectId, reviewDate: prevReviewDate },
              }),
              (WebAPI as any).request({
                query: `
                  query getPrevProjectSales($projectId: String, $useRDSData: Boolean, $reviewDate: String) {
                    getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
                      projectId
                      moldSalesByProject
                      developmentSalesByProject
                      equipmentSalesByProject
                      pricingAgreement
                      customerSchedule
                      equipmentInstallationSchedule
                      originalProfitAndLossByMonth
                      displayOriginalProfitAndLossByMonth
                    }
                  }
                `,
                variables: { projectId, useRDSData: true, reviewDate: prevReviewDate },
              }),
            ]);
            prevReviewPartNos = prevPartNoResp?.data?.getPartNo || [];
            prevReviewProjectData = prevProjResp?.data?.getProjectById || null;

            // 前回振り返りのシェア・スケジュールを抽出
            if (prevReviewProjectData) {
              if (prevReviewProjectData.pricingAgreement) {
                try {
                  const pa = JSON.parse(prevReviewProjectData.pricingAgreement);
                  setSharePrevious({
                    sharePercentage: pa?.competitor?.sharePercentage ?? null,
                    shareOfCompetitorAndCompany: pa?.competitor?.shareOfCompetitorAndCompany ?? null,
                  });
                } catch { /* skip */ }
              }
              if (prevReviewProjectData.customerSchedule) {
                try {
                  const parsed = JSON.parse(prevReviewProjectData.customerSchedule);
                  const normalized = parsed
                    .filter((item: any) => item.name && item.date)
                    .map((item: any) => ({
                      ...item,
                      id: String(item.id ?? "")
                    }));
                  setPrevScheduleItems(normalized);
                } catch { /* skip */ }
              }
              if (prevReviewProjectData.equipmentInstallationSchedule) {
                try {
                  const parsed = JSON.parse(prevReviewProjectData.equipmentInstallationSchedule);
                  const normalized = parsed
                    .filter((item: any) => item.name && item.startDate && item.endDate)
                    .map((item: any) => ({
                      ...item,
                      id: String(item.id ?? "")
                    }));
                  setPrevFacilityItems(normalized);
                } catch { /* skip */ }
              }
            }
          }
        } catch (reviewErr) {
          console.warn("振り返りデータ取得エラー:", reviewErr);
        }
      }

      setReviewDataList(reviewData);
      setHasReviewData(reviewPartNos.length > 0);

      // 計画値の取得（既存API）
      const response = await (WebAPI as any).request({
        query: `
          query getProjectForReview($projectId: String, $useRDSData: Boolean) {
            getProjectById(projectId: $projectId, useRDSData: $useRDSData) {
              projectId
              projectStartDate
              projectEndDate
              originalProfitAndLossByMonth
              displayOriginalProfitAndLossByMonth
              companyPlantSnapshot
              pricingAgreement
              customerSchedule
              equipmentInstallationSchedule
              reviewSchedule
              registrantDivisionId
            }
          }
        `,
        variables: { projectId, useRDSData: true },
      });

      // PartNoデータ取得（月別の数量・単価）
      const partNoResponse = await (WebAPI as any).request({
        query: `
          query getPartNo($projectId: String) {
            getPartNo(projectId: $projectId) {
              partNo
              quantityAndPrice
            }
          }
        `,
        variables: { projectId },
      });
      const partNoList = partNoResponse?.data?.getPartNo || [];

      const project = response?.data?.getProjectById;
      if (!project) {
        toast({ title: TITLE, description: t("common.message.projectNotFound"), status: 'warning', duration: 5000, isClosable: true });
        return;
      }

      if (project.registrantDivisionId) setRegistrantDivisionId(project.registrantDivisionId);

      if (!project.originalProfitAndLossByMonth) {
        toast({ title: TITLE, description: t("common.message.profitLossDataNotCreated"), status: 'warning', duration: 5000, isClosable: true });
        return;
      }

      // シェアデータ（申請時）を抽出
      if (project.pricingAgreement) {
        try {
          const pa = JSON.parse(project.pricingAgreement);
          setSharePlan({
            sharePercentage: pa?.competitor?.sharePercentage ?? null,
            shareOfCompetitorAndCompany: pa?.competitor?.shareOfCompetitorAndCompany ?? null,
          });
        } catch { /* skip */ }
      }

      // 計画スケジュール（マイルストーン対比用）
      if (project.customerSchedule) {
        try {
          const parsed = JSON.parse(project.customerSchedule);
          const normalized = parsed
            .filter((item: any) => item.name && item.date)
            .map((item: any) => ({
              ...item,
              id: String(item.id ?? "")
            }));
          setPlanScheduleItems(normalized);
        } catch { /* skip */ }
      }
      if (project.projectStartDate && project.projectEndDate) {
        setPlanProjectPeriod({ start: project.projectStartDate, end: project.projectEndDate });
      }
      // 計画・設備投資スケジュール（遅延原因参照用）
      if (project.equipmentInstallationSchedule) {
        try {
          const parsed = JSON.parse(project.equipmentInstallationSchedule);
          const normalized = parsed
            .filter((item: any) => item.name && item.startDate && item.endDate)
            .map((item: any) => ({
              ...item,
              id: String(item.id ?? "")
            }));
          setPlanFacilityItems(normalized);
        } catch { /* skip */ }
      }

      // ローカル通貨IDを取得
      const localCurrId = getLocalCurrencyId(project.companyPlantSnapshot);

      // 通貨マスタを取得（為替変換にも使用）
      // ProjectContext（親画面）で取得済みならそれを利用し、未取得（context未提供 or 空）の場合のみ自前で取得する
      let detectedLocalCurrency = CurrencyNameJPY;
      let currencyData: Currency[] = projectContext?.currencyData ?? [];
      try {
        if (localCurrId) {
          const localCurr = currencyData.find((c: any) => c.currencyId === localCurrId);
          if (localCurr?.currencyName) detectedLocalCurrency = localCurr.currencyName;
        }
      } catch { /* fallback to JPY */ }
      setLocalCurrencyName(detectedLocalCurrency);
      setCurrencyName(detectedLocalCurrency);

      // 振り返りモード: 月別レートで変換するラッパー関数
      // yearMonth 指定時は月別レート、未指定時は単一レートにフォールバック（対象PJ画面 L298 と同パターン）
      const convertWithRate = (
        amount: number | Decimal,
        from: string,
        to: string,
        yearMonth?: string
      ): Decimal => {
        if (reviewDateProp && yearMonth && monthlyRateMap && monthlyRateMap.size > 0) {
          return convertCurrencyByMonth(amount, from, to, yearMonth, monthlyRateMap, currencyData);
        }
        return convertCurrency(amount, from, to, currencyData);
      };

      // ローカル通貨版のP&Lデータ
      const plData = JSON.parse(project.originalProfitAndLossByMonth);
      // JPY版のP&Lデータ（存在すれば）
      const plDataJPY = project.displayOriginalProfitAndLossByMonth
        ? JSON.parse(project.displayOriginalProfitAndLossByMonth)
        : null;

      if (!plData.labels || plData.labels.length === 0) {
        toast({ title: TITLE, description: t("common.message.monthlyDataEmpty"), status: 'warning', duration: 5000, isClosable: true });
        return;
      }

      // 月ラベル統一（"YYYY/M"形式）
      let labels: string[] = plData.labels.map((l: string) => {
        const parts = l.split(/[-\/]/);
        if (parts.length >= 2) return `${parts[0]}/${parseInt(parts[1])}`;
        return l;
      });
      // BasicInfoのEOL予定日（現在値）に合わせてラベルを延長または短縮
      // propから渡された値（常に最新）を優先し、フォールバックとしてAPI取得値を使用
      labels = adjustLabelsToEOL(labels, projectEndDateProp ?? project.projectEndDate);
      setMonthLabels(labels);

      const totalMonths = labels.length;

      // 振り返り境界をreviewDatePropから算出
      let boundary = Math.min(18, totalMonths); // フォールバック
      if (reviewDateProp && labels.length > 0) {
        // reviewDatePropの年月をラベルと照合してインデックスを特定
        const reviewParts = reviewDateProp.split('-');
        const reviewLabel = `${reviewParts[0]}/${parseInt(reviewParts[1])}`;
        const foundIdx = labels.findIndex(l => l === reviewLabel);
        if (foundIdx >= 0) {
          boundary = foundIdx;
        }
      }
      let prevBoundary = 0;
      if (prevReviewDate) {
        const prevParts = prevReviewDate.split('-');
        const prevLabel = `${prevParts[0]}/${parseInt(prevParts[1])}`;
        const foundIdx = labels.findIndex(l => l === prevLabel);
        prevBoundary = foundIdx >= 0 ? foundIdx : 0;
      }
      setReviewBoundaryIndex(boundary);
      setPrevReviewIndex(prevBoundary);

      // 売上データ（DBから取得）
      // EOL変更によるラベル調整に合わせて配列長を揃える
      const totalSalesPlan = extendOrTruncate(
        plData.totalSales.map((v: string) => {
          try { return new Decimal(v || "0").toNumber(); } catch { return 0; }
        }),
        totalMonths
      );


      // 計画値の単価: 販売通貨→工場通貨に申請レート（単一レート）で変換
      // 申請時に確定した値なので、月別レートではなく申請レートを使う
      // convertFn を渡さない → getPricesByPartNoMonth 内部で convertCurrency にフォールバック
      const pricesByPartNoMonth = getPricesByPartNoMonth(
        partNoList,
        detectedLocalCurrency,
        currencyData,
        project.projectStartDate,
      );

      // 既存関数で月別数量を取得
      const quantitiesByPartNoMonth = getQuantitiesByPartNoMonth(
        partNoList,
        project.projectStartDate,
      );

      // PartNo別の単価を月別1次元配列に変換（最初のPartNo）
      let unitPricePlan: number[] = new Array(totalMonths).fill(0);
      if (pricesByPartNoMonth.length > 0) {
        const firstPartNo = pricesByPartNoMonth[0].partNo;
        const firstPartNoPrices = pricesByPartNoMonth.filter(p => p.partNo === firstPartNo);
        let idx = 0;
        for (const yearData of firstPartNoPrices) {
          for (const price of yearData.prices) {
            if (idx < totalMonths) {
              unitPricePlan[idx] = price.toNumber();
              idx++;
            }
          }
        }
      }

      // PartNo別の数量を合算して月別1次元配列に変換
      const totalQuantityPlan: number[] = new Array(totalMonths).fill(0);
      const partNos = [...new Set(quantitiesByPartNoMonth.map(q => q.partNo))];
      for (const pn of partNos) {
        const pnQuantities = quantitiesByPartNoMonth.filter(q => q.partNo === pn);
        let idx = 0;
        for (const yearData of pnQuantities) {
          for (const qty of yearData.quantities) {
            if (idx < totalMonths) {
              totalQuantityPlan[idx] += qty.toNumber();
              idx++;
            }
          }
        }
      }

      // 年度キー → カレンダー年月ラベル変換用（ローカル版・JPY版で共有）
      const startYear = project.projectStartDate
        ? parseInt(project.projectStartDate.split('-')[0])
        : 2025;
      const startMonth = project.projectStartDate
        ? parseInt(project.projectStartDate.split('-')[1])
        : 4;
      const fiscalStartYear = startMonth >= 4 ? startYear : startYear - 1;

      // プロジェクトレベル売上の通貨変換付き加算ヘルパー（ローカル版・JPY版で共有）
      // テーブル別の販売通貨→targetCurrencyに月別レート変換して加算
      const addProjectSales = (
        salesArray: number[],
        jsonStr: string | null | undefined,
        targetCurrency: string,
      ) => {
        if (!jsonStr) return;
        try {
          const salesData = JSON.parse(jsonStr);
          if (Array.isArray(salesData)) {
            for (const item of salesData) {
              if (!item.data) continue;
              const tableCurrencyId = item.currencyId;
              let sourceCurrency = "";
              if (tableCurrencyId) {
                const currency = currencyData.find(c => c.currencyId === tableCurrencyId);
                sourceCurrency = currency?.currencyName || "";
              }
              const years = Object.keys(item.data).sort((a, b) => Number(a) - Number(b));
              for (const yearKey of years) {
                const yearData = item.data[yearKey];
                if (!yearData?.months) continue;
                const fiscalYear = fiscalStartYear + (Number(yearKey) - 1);
                for (let m = 1; m <= 12; m++) {
                  const monthData = yearData.months[m];
                  if (!monthData) continue;
                  const calMonth = m <= 9 ? m + 3 : m - 9;
                  const calYear = m <= 9 ? fiscalYear : fiscalYear + 1;
                  const lbl = `${calYear}/${calMonth}`;
                  const idx = labels.indexOf(lbl);
                  if (idx >= 0 && idx < totalMonths) {
                    const rawPrice = Number(monthData.price) || 0;
                    const yearMonth = `${calYear}-${String(calMonth).padStart(2, "0")}`;
                    const converted = sourceCurrency && targetCurrency
                      ? convertWithRate(rawPrice, sourceCurrency, targetCurrency, yearMonth).toNumber()
                      : rawPrice;
                    salesArray[idx] += converted;
                  }
                }
              }
            }
          }
        } catch { /* skip */ }
      };

      // PartNo別売上（金型・開発費）の通貨変換付き加算ヘルパー
      // PartNoの主要通貨（quantityAndPrice.currencyAndRatio の最大比率）→ targetCurrency に月別レート変換して加算
      // 対象PJ画面（chartDataCalculation.ts: generateMonthlySalesByPartNoModified）と同等の集計
      const addPartNoSales = (
        salesArray: number[],
        partNoArr: any[],
        fieldName: 'moldSalesByPartNo' | 'developmentSalesByPartNo',
        targetCurrency: string,
      ) => {
        for (const partNoItem of partNoArr) {
          const jsonStr = partNoItem?.[fieldName];
          if (!jsonStr) continue;

          // PartNoの主要通貨を判定（最大比率）
          let partNoCurrency = "";
          if (partNoItem.quantityAndPrice) {
            try {
              const qp = JSON.parse(partNoItem.quantityAndPrice);
              const ratioRaw = qp?.currencyAndRatio;
              const ratio = typeof ratioRaw === 'string' ? JSON.parse(ratioRaw) : ratioRaw;
              if (Array.isArray(ratio) && ratio.length > 0) {
                const maxRatio = Math.max(...ratio.map((c: any) => c.ratio || 0));
                const max = ratio.find((c: any) => c.ratio === maxRatio);
                if (max?.id) {
                  const cur = currencyData.find(c => c.currencyId === max.id);
                  if (cur?.currencyName) partNoCurrency = cur.currencyName;
                }
              }
            } catch { /* skip */ }
          }

          try {
            const items = JSON.parse(jsonStr);
            if (!Array.isArray(items)) continue;
            for (const item of items) {
              if (!item?.data) continue;
              const years = Object.keys(item.data).sort((a, b) => Number(a) - Number(b));
              for (const yearKey of years) {
                const yearData = item.data[yearKey];
                if (!yearData?.months) continue;
                const fiscalYear = fiscalStartYear + (Number(yearKey) - 1);
                for (let m = 1; m <= 12; m++) {
                  const monthData = yearData.months[m];
                  if (!monthData) continue;
                  const calMonth = m <= 9 ? m + 3 : m - 9;
                  const calYear = m <= 9 ? fiscalYear : fiscalYear + 1;
                  const lbl = `${calYear}/${calMonth}`;
                  const idx = labels.indexOf(lbl);
                  if (idx >= 0 && idx < totalMonths) {
                    const rawPrice = Number(monthData.price) || 0;
                    if (rawPrice === 0) continue;
                    const yearMonth = `${calYear}-${String(calMonth).padStart(2, "0")}`;
                    const converted = partNoCurrency && targetCurrency
                      ? convertWithRate(rawPrice, partNoCurrency, targetCurrency, yearMonth).toNumber()
                      : rawPrice;
                    salesArray[idx] += converted;
                  }
                }
              }
            }
          } catch { /* skip */ }
        }
      };

      // 振り返りデータの有無でデータ生成方法を切り替え
      let salesReview: ReviewComparisonData;
      let unitPriceReview: ReviewComparisonData;
      let totalQtyReview: ReviewComparisonData;

      if (reviewPartNos.length > 0) {
        // 振り返りPartNoデータから実際の数量・単価を抽出（販売通貨→工場通貨に月別レート変換）
        const reviewPrices = getPricesByPartNoMonth(
          reviewPartNos,
          detectedLocalCurrency,
          currencyData,
          project.projectStartDate,
          convertWithRate,
        );
        const reviewQuantities = getQuantitiesByPartNoMonth(
          reviewPartNos,
          project.projectStartDate,
        );

        // 振り返りデータから月別単価を構築
        let reviewUnitPrice: number[] = new Array(totalMonths).fill(0);
        if (reviewPrices.length > 0) {
          const firstPartNo = reviewPrices[0].partNo;
          const firstPartNoPrices = reviewPrices.filter(p => p.partNo === firstPartNo);
          let idx = 0;
          for (const yearData of firstPartNoPrices) {
            for (const price of yearData.prices) {
              if (idx < totalMonths) {
                reviewUnitPrice[idx] = price.toNumber();
                idx++;
              }
            }
          }
        }

        // 振り返りデータから月別数量を構築（全PartNo合算）
        const reviewTotalQuantity: number[] = new Array(totalMonths).fill(0);
        const reviewPns = [...new Set(reviewQuantities.map(q => q.partNo))];
        for (const pn of reviewPns) {
          const pnQuantities = reviewQuantities.filter(q => q.partNo === pn);
          let idx = 0;
          for (const yearData of pnQuantities) {
            for (const qty of yearData.quantities) {
              if (idx < totalMonths) {
                reviewTotalQuantity[idx] += qty.toNumber();
                idx++;
              }
            }
          }
        }

        // 振り返り売上を計算: 製品売上（数量×単価）＋ 金型売上 ＋ 開発費売上 ＋ 設備売上
        // P&L画面の保存を待たずに、数量・単価画面のデータだけで算出
        // 単価は reviewPrices で工場通貨に月別レート変換済み
        const reviewSalesData = new Array(totalMonths).fill(0);

        // 製品売上: PartNo別に reviewQuantities（生数量） × reviewPrices（変換済み単価）を月別合算
        for (const pn of reviewPns) {
          const pnQtys = reviewQuantities.filter(q => q.partNo === pn);
          const pnPrices = reviewPrices.filter(p => p.partNo === pn);
          let idx = 0;
          for (let yIdx = 0; yIdx < pnQtys.length; yIdx++) {
            const qtys = pnQtys[yIdx]?.quantities || [];
            const prices = pnPrices[yIdx]?.prices || [];
            for (let mIdx = 0; mIdx < qtys.length; mIdx++) {
              if (idx < totalMonths) {
                const qty = qtys[mIdx]?.toNumber() || 0;
                const price = prices[mIdx]?.toNumber() || 0;
                reviewSalesData[idx] += qty * price;
                idx++;
              }
            }
          }
        }

        // 金型・開発費・設備売上をプロジェクトレベルで加算（targetCurrency=工場通貨）
        const latestReview = reviewData[0];

        if (latestReview) {
          addProjectSales(reviewSalesData, latestReview.moldSalesByProject, detectedLocalCurrency);
          addProjectSales(reviewSalesData, latestReview.developmentSalesByProject, detectedLocalCurrency);
          addProjectSales(reviewSalesData, latestReview.equipmentSalesByProject, detectedLocalCurrency);
        }

        // PartNo別の金型・開発費売上を加算（対象PJ画面と同等の集計）
        addPartNoSales(reviewSalesData, reviewPartNos, 'moldSalesByPartNo', detectedLocalCurrency);
        addPartNoSales(reviewSalesData, reviewPartNos, 'developmentSalesByPartNo', detectedLocalCurrency);

        // 前回値の計算（前回振り返りが存在する場合、なければ計画値をデフォルトとして使用）
        let prevSalesData: number[] = totalSalesPlan;
        let prevUnitPrice: number[] = unitPricePlan;
        let prevTotalQty: number[] = totalQuantityPlan;

        let prevPrices = [] as typeof pricesByPartNoMonth;
        let prevQuantities = [] as typeof quantitiesByPartNoMonth;
        if (prevReviewPartNos.length > 0) {
          prevPrices = getPricesByPartNoMonth(prevReviewPartNos, detectedLocalCurrency, currencyData, project.projectStartDate, convertWithRate);
          prevQuantities = getQuantitiesByPartNoMonth(prevReviewPartNos, project.projectStartDate);

          // 前回単価
          prevUnitPrice = new Array(totalMonths).fill(0);
          if (prevPrices.length > 0) {
            const firstPrevPartNo = prevPrices[0].partNo;
            const firstPrevPartNoPrices = prevPrices.filter(p => p.partNo === firstPrevPartNo);
            let idx = 0;
            for (const yearData of firstPrevPartNoPrices) {
              for (const price of yearData.prices) {
                if (idx < totalMonths) { prevUnitPrice[idx] = price.toNumber(); idx++; }
              }
            }
          }

          // 前回数量
          prevTotalQty = new Array(totalMonths).fill(0);
          const prevPns = [...new Set(prevQuantities.map(q => q.partNo))];
          for (const pn of prevPns) {
            const pnQtys = prevQuantities.filter(q => q.partNo === pn);
            let idx = 0;
            for (const yearData of pnQtys) {
              for (const qty of yearData.quantities) {
                if (idx < totalMonths) { prevTotalQty[idx] += qty.toNumber(); idx++; }
              }
            }
          }

          // 前回売上: 前回振り返り時点の対象PJ保存値（originalProfitAndLossByMonth.totalSales）を流用
          // 月別レート適用済み・工場通貨建てのスナップショット → ラベル整列するだけ
          prevSalesData = new Array(totalMonths).fill(0);
          if (prevReviewProjectData?.originalProfitAndLossByMonth) {
            try {
              const prevPlData = JSON.parse(prevReviewProjectData.originalProfitAndLossByMonth);
              if (prevPlData?.totalSales && Array.isArray(prevPlData.totalSales) && prevPlData.labels) {
                // ラベル形式を正規化（"YYYY/M"）
                const prevLabels: string[] = prevPlData.labels.map((l: string) => {
                  const parts = l.split(/[-\/]/);
                  if (parts.length >= 2) return `${parts[0]}/${parseInt(parts[1])}`;
                  return l;
                });
                const prevTotalSales: number[] = prevPlData.totalSales.map((v: string) => {
                  try { return new Decimal(v || "0").toNumber(); } catch { return 0; }
                });
                prevSalesData = labels.map((lbl) => {
                  const idx = prevLabels.indexOf(lbl);
                  return idx >= 0 ? (prevTotalSales[idx] || 0) : 0;
                });
              }
            } catch { /* parse error → 0埋めのまま */ }
          }
        }

        // 今回値 = 振り返りデータ、前回値 = 前回振り返り（初回の場合は計画値）
        salesReview = { originalPlan: totalSalesPlan, previous: prevSalesData, current: reviewSalesData };
        unitPriceReview = { originalPlan: unitPricePlan, previous: prevUnitPrice, current: reviewUnitPrice };
        totalQtyReview = { originalPlan: totalQuantityPlan, previous: prevTotalQty, current: reviewTotalQuantity };

        // PartNo別グラフデータを構築（PartNo数にかかわらず全件構築する）
        const allPartNos: string[] = [...new Set<string>(partNoList.map((p: any) => p.partNo as string))];
        setSalesPartNos(allPartNos);
        setSelectedPartNoPriceGraph(allPartNos[0] ?? "");

        {
          const priceByPN: Record<string, ReviewComparisonData> = {};
          const qtyByPN: Record<string, ReviewComparisonData> = {};

          for (const pn of allPartNos) {
            const planPriceData = pricesByPartNoMonth.filter((p: any) => p.partNo === pn);
            const planQtyData = quantitiesByPartNoMonth.filter((q: any) => q.partNo === pn);

            const planPriceArr: number[] = new Array(totalMonths).fill(0);
            let idx2 = 0;
            for (const yd of planPriceData) {
              for (const price of (yd as any).prices) {
                if (idx2 < totalMonths) { planPriceArr[idx2] = (price as any).toNumber(); idx2++; }
              }
            }

            const planQtyArr: number[] = new Array(totalMonths).fill(0);
            idx2 = 0;
            for (const yd of planQtyData) {
              for (const qty of (yd as any).quantities) {
                if (idx2 < totalMonths) { planQtyArr[idx2] += (qty as any).toNumber(); idx2++; }
              }
            }

            const revPriceData = reviewPrices.filter((p: any) => p.partNo === pn);
            const revQtyData = reviewQuantities.filter((q: any) => q.partNo === pn);

            const revPriceArr: number[] = new Array(totalMonths).fill(0);
            idx2 = 0;
            for (const yd of revPriceData) {
              for (const price of (yd as any).prices) {
                if (idx2 < totalMonths) { revPriceArr[idx2] = (price as any).toNumber(); idx2++; }
              }
            }

            const revQtyArr: number[] = new Array(totalMonths).fill(0);
            idx2 = 0;
            for (const yd of revQtyData) {
              for (const qty of (yd as any).quantities) {
                if (idx2 < totalMonths) { revQtyArr[idx2] += (qty as any).toNumber(); idx2++; }
              }
            }

            let prevPriceArr: number[] = prevPrices.length > 0 ? new Array(totalMonths).fill(0) : [...planPriceArr];
            let prevQtyArr: number[] = prevQuantities.length > 0 ? new Array(totalMonths).fill(0) : [...planQtyArr];

            if (prevPrices.length > 0) {
              const prevPriceData2 = prevPrices.filter((p: any) => p.partNo === pn);
              idx2 = 0;
              for (const yd of prevPriceData2) {
                for (const price of (yd as any).prices) {
                  if (idx2 < totalMonths) { prevPriceArr[idx2] = (price as any).toNumber(); idx2++; }
                }
              }
            }

            if (prevQuantities.length > 0) {
              const prevQtyData2 = prevQuantities.filter((q: any) => q.partNo === pn);
              idx2 = 0;
              for (const yd of prevQtyData2) {
                for (const qty of (yd as any).quantities) {
                  if (idx2 < totalMonths) { prevQtyArr[idx2] += (qty as any).toNumber(); idx2++; }
                }
              }
            }

            priceByPN[pn] = { originalPlan: planPriceArr, previous: prevPriceArr, current: revPriceArr };
            qtyByPN[pn] = { originalPlan: planQtyArr, previous: prevQtyArr, current: revQtyArr };
          }

          setPriceDataByPartNo(priceByPN);
          setQtyDataByPartNo(qtyByPN);
        }
      } else {
        // 振り返りデータなし → 計画値のみ表示（前回値・今回値は空）
        const emptyArray = new Array(totalMonths).fill(0);
        salesReview = { originalPlan: totalSalesPlan, previous: emptyArray, current: emptyArray };
        unitPriceReview = { originalPlan: unitPricePlan, previous: emptyArray, current: emptyArray };
        totalQtyReview = { originalPlan: totalQuantityPlan, previous: emptyArray, current: emptyArray };
      }

      // ローカル通貨版のチャートセクション構築
      const buildSections = (salesData: ReviewComparisonData, priceData: ReviewComparisonData, qtyData: ReviewComparisonData, curr: string): ChartSectionData[] => [
        {
          id: "salesAmount",
          title: t("features.InvestmentScreening.Review.ReviewSales.chartSection.salesAmount"),
          unit: curr,
          summaryType: "total",
          data: salesData,
        },
        {
          id: "salesPrice",
          title: t("features.InvestmentScreening.Review.ReviewSales.chartSection.salesPrice"),
          unit: curr,
          summaryType: "average",
          data: priceData,
        },
        {
          id: "salesQuantity",
          title: t("features.InvestmentScreening.Review.ReviewSales.chartSection.salesQuantity"),
          unit: "pcs",
          summaryType: "total",
          data: qtyData,
        },
      ];

      const localSections = buildSections(salesReview, unitPriceReview, totalQtyReview, detectedLocalCurrency);
      setChartSectionsLocal(localSections);

      // JPY版のチャートセクション構築
      // 計画値・前回値の売上はスナップショット流用（対象PJ画面で月別レート適用済み）、今回値の売上は都度計算、単価は3パターン全て月別レート変換
      if (plDataJPY && plDataJPY.totalSales) {
        // 計画値の売上（JPY）: 申請時スナップショット
        const jpySalesPlan = plDataJPY.totalSales.map((v: string) => {
          try { return new Decimal(v || "0").toNumber(); } catch { return 0; }
        });

        // 計画値の単価（JPY）: 販売通貨→JPYに申請レート（単一レート）で変換
        // 申請時に確定した値なので、月別レートではなく申請レートを使う
        const jpyPlanPrices = getPricesByPartNoMonth(
          partNoList,
          CurrencyNameJPY,
          currencyData,
          project.projectStartDate,
        );
        const jpyUnitPricePlan: number[] = new Array(totalMonths).fill(0);
        if (jpyPlanPrices.length > 0) {
          const firstPartNo = jpyPlanPrices[0].partNo;
          const firstPartNoPrices = jpyPlanPrices.filter(p => p.partNo === firstPartNo);
          let idx = 0;
          for (const yearData of firstPartNoPrices) {
            for (const price of yearData.prices) {
              if (idx < totalMonths) { jpyUnitPricePlan[idx] = price.toNumber(); idx++; }
            }
          }
        }

        let jpySalesReview: ReviewComparisonData;
        let jpyUnitPriceReview: ReviewComparisonData;

        if (reviewPartNos.length > 0) {
          // 今回値の単価（JPY）
          const jpyReviewPrices = getPricesByPartNoMonth(
            reviewPartNos,
            CurrencyNameJPY,
            currencyData,
            project.projectStartDate,
            convertWithRate,
          );
          const jpyReviewQuantities = getQuantitiesByPartNoMonth(
            reviewPartNos,
            project.projectStartDate,
          );
          const jpyReviewUnitPrice: number[] = new Array(totalMonths).fill(0);
          if (jpyReviewPrices.length > 0) {
            const firstPartNo = jpyReviewPrices[0].partNo;
            const firstPartNoPrices = jpyReviewPrices.filter(p => p.partNo === firstPartNo);
            let idx = 0;
            for (const yearData of firstPartNoPrices) {
              for (const price of yearData.prices) {
                if (idx < totalMonths) { jpyReviewUnitPrice[idx] = price.toNumber(); idx++; }
              }
            }
          }

          // 今回値の売上（JPY）: jpyReviewPrices × reviewQuantities + プロジェクトレベル売上(target=JPY)
          const jpyReviewSales = new Array(totalMonths).fill(0);
          const jpyPns = [...new Set(jpyReviewQuantities.map(q => q.partNo))];
          for (const pn of jpyPns) {
            const pnQtys = jpyReviewQuantities.filter(q => q.partNo === pn);
            const pnPrices = jpyReviewPrices.filter(p => p.partNo === pn);
            let idx = 0;
            for (let yIdx = 0; yIdx < pnQtys.length; yIdx++) {
              const qtys = pnQtys[yIdx]?.quantities || [];
              const prices = pnPrices[yIdx]?.prices || [];
              for (let mIdx = 0; mIdx < qtys.length; mIdx++) {
                if (idx < totalMonths) {
                  const qty = qtys[mIdx]?.toNumber() || 0;
                  const price = prices[mIdx]?.toNumber() || 0;
                  jpyReviewSales[idx] += qty * price;
                  idx++;
                }
              }
            }
          }
          const latestReview = reviewData[0];
          if (latestReview) {
            addProjectSales(jpyReviewSales, latestReview.moldSalesByProject, CurrencyNameJPY);
            addProjectSales(jpyReviewSales, latestReview.developmentSalesByProject, CurrencyNameJPY);
            addProjectSales(jpyReviewSales, latestReview.equipmentSalesByProject, CurrencyNameJPY);
          }

          // PartNo別の金型・開発費売上を加算（JPY版）
          addPartNoSales(jpyReviewSales, reviewPartNos, 'moldSalesByPartNo', CurrencyNameJPY);
          addPartNoSales(jpyReviewSales, reviewPartNos, 'developmentSalesByPartNo', CurrencyNameJPY);

          // 前回値（JPY）: 売上=前回振り返り対象PJスナップショット、単価=前回振り返り時のPartNoから月別レート変換
          let jpyPrevSales: number[] = jpySalesPlan;
          let jpyPrevUnitPrice: number[] = jpyUnitPricePlan;
          let jpyPrevPrices: ReturnType<typeof getPricesByPartNoMonth> = [];
          if (prevReviewPartNos.length > 0) {
            jpyPrevPrices = getPricesByPartNoMonth(
              prevReviewPartNos,
              CurrencyNameJPY,
              currencyData,
              project.projectStartDate,
              convertWithRate,
            );
            jpyPrevUnitPrice = new Array(totalMonths).fill(0);
            if (jpyPrevPrices.length > 0) {
              const firstPartNo = jpyPrevPrices[0].partNo;
              const firstPartNoPrices = jpyPrevPrices.filter(p => p.partNo === firstPartNo);
              let idx = 0;
              for (const yearData of firstPartNoPrices) {
                for (const price of yearData.prices) {
                  if (idx < totalMonths) { jpyPrevUnitPrice[idx] = price.toNumber(); idx++; }
                }
              }
            }

            // 前回売上（JPY）: 前回振り返り時点の対象PJスナップショット流用
            jpyPrevSales = new Array(totalMonths).fill(0);
            if (prevReviewProjectData?.displayOriginalProfitAndLossByMonth) {
              try {
                const prevPlDataJPY = JSON.parse(prevReviewProjectData.displayOriginalProfitAndLossByMonth);
                if (prevPlDataJPY?.totalSales && Array.isArray(prevPlDataJPY.totalSales) && prevPlDataJPY.labels) {
                  const prevLabelsJPY: string[] = prevPlDataJPY.labels.map((l: string) => {
                    const parts = l.split(/[-\/]/);
                    if (parts.length >= 2) return `${parts[0]}/${parseInt(parts[1])}`;
                    return l;
                  });
                  const prevTotalSalesJPY: number[] = prevPlDataJPY.totalSales.map((v: string) => {
                    try { return new Decimal(v || "0").toNumber(); } catch { return 0; }
                  });
                  jpyPrevSales = labels.map((lbl) => {
                    const idx = prevLabelsJPY.indexOf(lbl);
                    return idx >= 0 ? (prevTotalSalesJPY[idx] || 0) : 0;
                  });
                }
              } catch { /* parse error → 0埋めのまま */ }
            }
          }

          // JPY版 PartNo別単価データを構築（ローカル通貨版と同じ構造、convertWithRateで月別レート変換済み）
          // PartNo数にかかわらず全件構築する（申請書側で表示通貨に変換して利用するため）
          const jpyAllPartNos = [...new Set<string>(partNoList.map((p: any) => p.partNo as string))];
          {
            const priceByPNJPY: Record<string, ReviewComparisonData> = {};
            for (const pn of jpyAllPartNos) {
              const planPriceData = jpyPlanPrices.filter(p => p.partNo === pn);
              const planPriceArr: number[] = new Array(totalMonths).fill(0);
              let idx2 = 0;
              for (const yd of planPriceData) {
                for (const price of yd.prices) {
                  if (idx2 < totalMonths) { planPriceArr[idx2] = price.toNumber(); idx2++; }
                }
              }

              const revPriceData = jpyReviewPrices.filter(p => p.partNo === pn);
              const revPriceArr: number[] = new Array(totalMonths).fill(0);
              idx2 = 0;
              for (const yd of revPriceData) {
                for (const price of yd.prices) {
                  if (idx2 < totalMonths) { revPriceArr[idx2] = price.toNumber(); idx2++; }
                }
              }

              let prevPriceArr: number[] = jpyPrevPrices.length > 0 ? new Array(totalMonths).fill(0) : [...planPriceArr];
              if (jpyPrevPrices.length > 0) {
                const prevPriceData = jpyPrevPrices.filter(p => p.partNo === pn);
                idx2 = 0;
                for (const yd of prevPriceData) {
                  for (const price of yd.prices) {
                    if (idx2 < totalMonths) { prevPriceArr[idx2] = price.toNumber(); idx2++; }
                  }
                }
              }

              priceByPNJPY[pn] = { originalPlan: planPriceArr, previous: prevPriceArr, current: revPriceArr };
            }
            setPriceDataByPartNoJPY(priceByPNJPY);
          }

          jpySalesReview = { originalPlan: jpySalesPlan, previous: jpyPrevSales, current: jpyReviewSales };
          jpyUnitPriceReview = { originalPlan: jpyUnitPricePlan, previous: jpyPrevUnitPrice, current: jpyReviewUnitPrice };
        } else {
          const emptyArray = new Array(totalMonths).fill(0);
          jpySalesReview = { originalPlan: jpySalesPlan, previous: emptyArray, current: emptyArray };
          jpyUnitPriceReview = { originalPlan: jpyUnitPricePlan, previous: emptyArray, current: emptyArray };
        }

        const jpySections = buildSections(jpySalesReview, jpyUnitPriceReview, totalQtyReview, CurrencyNameJPY);
        setChartSectionsJPY(jpySections);
      }

      // デフォルトはローカル通貨
      setChartSections(localSections);

      // 各データソースを個別にチェックし、不足分だけtoastを表示（既存画面のforEach toastパターンに準拠）
      const displayNameArray: string[] = [];

      // 数量・単価データのチェック
      if (reviewPartNos.length === 0) {
        displayNameArray.push(t("features.InvestmentScreening.Review.ReviewSales.screenName.salesQuantity"));
      }

      // 顧客取り決め事項のチェック（シェア含む）
      // 振り返りプロジェクトデータにpricingAgreementが含まれているかで判定
      const latestReviewForCheck = reviewData.length > 0 ? reviewData[0] : null;
      if (!latestReviewForCheck?.pricingAgreement) {
        displayNameArray.push(t("features.InvestmentScreening.Review.ReviewSales.screenName.customerTerms"));
      }

      if (displayNameArray.length > 0) {
        displayNameArray.forEach((displayName: string) => {
          toast({
            title: TITLE,
            description: t("common.message.pleaseRegisterFrom", { name: displayName }),
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        });
      }

      // reviewSchedule を解析してフェーズ名取得ヘルパーを用意
      let salesScheduleItems: { date: string; phase?: string }[] = [];
      if (project.reviewSchedule) {
        try { salesScheduleItems = JSON.parse(project.reviewSchedule); } catch { /* skip */ }
      }
      const getSalesPhaseForDate = (date: string | null | undefined): string | undefined => {
        if (!date) return undefined;
        const ym = date.substring(0, 7);
        return salesScheduleItems.find(s => s.date && s.date.startsWith(ym))?.phase;
      };

      // マイルストーン（申請時 → 過去振り返り → ★今回 → 将来の全件 → EOL）
      const ms: { label: string; idx: number; isCurrent?: boolean }[] = [{
        label: t("features.InvestmentScreening.Review.common.milestoneApplication", { label: formatYearMonthByLocale(labels[0]) }),
        idx: 0,
      }];
      // 過去の振り返り全件（今回より前、申請時=idx0 を除く）を時系列順で追加
      const pastSalesItems = salesScheduleItems
        .filter(s => s.date && reviewDateProp && s.date < reviewDateProp)
        .sort((a, b) => a.date.localeCompare(b.date));
      for (const s of pastSalesItems) {
        const parts = s.date.split('-');
        const lbl = `${parts[0]}/${parseInt(parts[1])}`;
        const foundIdx = labels.findIndex(l => l === lbl);
        if (foundIdx > 0 && foundIdx < totalMonths) {
          ms.push({
            label: s.phase
              ? t("features.InvestmentScreening.Review.common.milestonePhase", { phase: s.phase, label: formatYearMonthByLocale(labels[foundIdx]) })
              : t("features.InvestmentScreening.Review.common.milestoneReview", { label: formatYearMonthByLocale(labels[foundIdx]) }),
            idx: foundIdx,
          });
        }
      }
      const currentPhase = getSalesPhaseForDate(reviewDateProp);
      const currentLabel = formatYearMonthByLocale(labels[Math.min(boundary, totalMonths - 1)]);
      ms.push({
        label: currentPhase
          ? t("features.InvestmentScreening.Review.ReviewSales.milestone.currentReviewWithPhase", { phase: currentPhase, label: currentLabel })
          : t("features.InvestmentScreening.Review.common.milestoneCurrentReview", { label: currentLabel }),
        idx: boundary,
        isCurrent: true
      });
      // 将来の振り返りスケジュール全件を追加し、最初の次回日をデフォルト終了に使う
      const futureSalesItems = salesScheduleItems
        .filter(s => s.date && reviewDateProp && s.date > reviewDateProp)
        .sort((a, b) => a.date.localeCompare(b.date));
      let nextSalesBoundary = totalMonths; // デフォルト終了: 次回振り返り日（なければ全期間末）
      for (const s of futureSalesItems) {
        const parts = s.date.split('-');
        const lbl = `${parts[0]}/${parseInt(parts[1])}`;
        const foundIdx = labels.findIndex(l => l === lbl);
        if (foundIdx >= 0 && foundIdx < totalMonths) {
          if (nextSalesBoundary === totalMonths) nextSalesBoundary = foundIdx; // 最初の次回
          ms.push({
            label: s.phase
              ? t("features.InvestmentScreening.Review.common.milestonePhase", { phase: s.phase, label: formatYearMonthByLocale(labels[foundIdx]) })
              : t("features.InvestmentScreening.Review.common.milestoneReview", { label: formatYearMonthByLocale(labels[foundIdx]) }),
            idx: foundIdx,
          });
        }
      }
      ms.push({
        label: t("features.InvestmentScreening.Review.ReviewSales.milestone.eolWithLabel", { label: formatYearMonthByLocale(labels[totalMonths - 1]) }),
        idx: totalMonths,
      });
      setMilestones(ms);

      // デフォルト期間（仕様: 開始=前回振り返り日・初回は申請時、終了=次回振り返り日）
      setPeriodStart(prevBoundary > 0 ? prevBoundary : 0);
      setPeriodEnd(nextSalesBoundary);

    } catch (err) {
      console.error("振り返り分析データ取得エラー:", err);
      toast({ title: TITLE, description: t("features.InvestmentScreening.Review.ReviewSales.schedule.fetchError"), status: 'error', duration: 5000, isClosable: true });
    }
  };

  // ===== マイルストーン差分計算 =====
  const calcDiffDays = (planDate: string, actualDate: string): number =>
    dayjs(actualDate).diff(dayjs(planDate), 'day');

  const formatDateStr = (dateStr: string): string => {
    if (!dateStr) return "—";
    const parts = dateStr.split('-');
    if (parts.length >= 3) return formatDateByLocale(dateStr);
    if (parts.length >= 2) return formatYearMonthByLocale(dateStr);
    return dateStr;
  };

  const formatMonthStr = (dateStr: string): string => {
    return formatYearMonthByLocale(dateStr);
  };

  const formatDiff = (days: number): string => {
    if (days === 0) return t("common.label.noChange");
    const sign = days > 0 ? "+" : "";
    const absDays = Math.abs(days);
    if (absDays >= 30) {
      const months = Math.round(days / 30.4);
      return t("features.InvestmentScreening.Review.common.diffMonths", { sign, months });
    }
    return t("features.InvestmentScreening.Review.common.diffDays", { sign, days });
  };

  // ===== チャート生成 =====
  const createChartData = (
    section: ChartSectionData,
    start: number,
    end: number,
  ) => {
    const compareWith = compareMode === "previous" ? section.data.previous : section.data.originalPlan;
    const compareLabel = compareMode === "previous"
      ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.originalPlan"))
      : t("common.label.originalPlan");

    return {
      labels: monthLabels.slice(start, end).map(formatYearMonthByLocale),
      datasets: [
        {
          label: compareLabel,
          data: compareWith.slice(start, end),
          backgroundColor: compareColors[compareMode].bg,
          borderColor: compareColors[compareMode].border,
          borderWidth: 1,
        },
        {
          label: t("common.label.currentValue"),
          data: section.data.current.slice(start, end),
          backgroundColor: "rgba(237, 137, 54, 0.7)",
          borderColor: "rgba(237, 137, 54, 1)",
          borderWidth: 1,
        },
      ],
    };
  };

  const createChartOptions = (section: ChartSectionData, start: number, end: number) => {
    const adjustedBoundary = reviewBoundaryIndex - start;
    const showBoundary = adjustedBoundary >= 0 && adjustedBoundary < (end - start);
    // 実際に描画する2系列だけで Y 軸最大値を決める（表示していない系列の外れ値で余白が生じるのを防ぐ）
    const displayedCompare = compareMode === "previous" ? section.data.previous : section.data.originalPlan;
    const displayedValues = [
      ...displayedCompare.slice(start, end),
      ...section.data.current.slice(start, end),
    ].filter(v => isFinite(v));
    const unifiedMax = Math.ceil((Math.max(...displayedValues, 0)) * 1.1) || 1;

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top" as const },
        datalabels: false as const,
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const value = context.raw as number;
              const isMoney = section.unit !== "pcs";
              if (!isMoney) return `${context.dataset.label}: ${Math.round(value).toLocaleString()}`;
              const dp = hasDecimal(currencyName) ? 4 : 0;
              let formatted = formatNumber(value, currencyName, dp);
              if (hasDecimal(currencyName) && formatted.includes('.')) {
                formatted = formatted.replace(/0+$/, '').replace(/\.$/, '');
              }
              return `${context.dataset.label}: ${formatted}`;
            },
          },
        },
        annotation: {
          annotations: showBoundary ? {
            reviewBoundary: {
              type: "line" as const,
              xMin: adjustedBoundary - 0.5,
              xMax: adjustedBoundary - 0.5,
              borderColor: "rgba(229, 62, 62, 0.8)",
              borderWidth: 2,
              borderDash: [6, 4],
              label: {
                display: true,
                content: t("common.label.review"),
                position: "start" as const,
                backgroundColor: "rgba(229, 62, 62, 0.8)",
                color: "white",
                font: { size: 10 },
              },
            },
          } : {},
        },
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 45,
            autoSkip: true,
            maxTicksLimit: Math.min(12, end - start),
            font: { size: 10 },
          },
        },
        y: {
          beginAtZero: true,
          suggestedMax: unifiedMax,
          ticks: { maxTicksLimit: 8 },
        },
      },
    };
  };

  // ========== salesChartData 構築 ==========
  // 申請書画面（ReportAndDraft）の振り返りモードで再描画するためのデータを Chart.js 形式に変換して保存する。
  // ローカル通貨1本で保存し、申請書側で convertChartDatasets により表示通貨へ動的変換する（Simulationと同パターン）。
  // ただし、数量以外のsummaryValuesを除く
  // previousMode / originalPlanMode の両方を保持することで compareMode 切替もカバー。
  //
  // 販売単価 / 販売数量は PartNo 別に分解して保存する（申請書側で PartNo ごとに縦並べ表示）。
  // 売上計画の対比のみ全体集計（合算）として sections に残す。
  /**
   * 申請書画面（ReportAndDraft）の振り返りセクションで使用する営業視点グラフデータを構築して返す。
   * ローカル通貨1本で保存し、申請書側で convertChartDatasets により表示通貨へ動的変換する。
   * sections（売上計画対比）+ pricePartNoSections（PartNo別単価）+ qtyPartNoSections（PartNo別数量）の
   * 3種を previousMode / originalPlanMode の両方で保持する。
   * @returns 申請書側に保存する営業視点グラフデータオブジェクト
   */
  const buildSalesChartData = () => {
    /**
     * 1セクション分の Bar グラフデータを比較モードに合わせて生成する
     * @param data 計画値・前回値・今回値を含む比較データ
     * @param mode 比較モード（"previous": 前回値との比較 / "originalPlan": 申請時計画との比較）
     * @returns Chart.js 用の labels + datasets オブジェクト
     */
    const buildChartDataForMode = (data: ReviewComparisonData, mode: CompareMode) => {
      const compareWith = mode === "previous" ? data.previous : data.originalPlan;
      const compareLabel = compareMode === "previous"
        ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.originalPlan"))
        : t("common.label.originalPlan");
      return {
        labels: monthLabels.map(formatYearMonthByLocale),
        datasets: [
          {
            label: compareLabel,
            data: compareWith,
            backgroundColor: compareColors[mode].bg,
            borderColor: compareColors[mode].border,
            borderWidth: 1,
          },
          {
            label: t("common.label.currentValue"),
            data: data.current,
            backgroundColor: "rgba(237, 137, 54, 0.7)",
            borderColor: "rgba(237, 137, 54, 1)",
            borderWidth: 1,
          },
        ],
      };
    };

    /**
     * 1セクション分のサマリーカード値（達成率・予測精度・最終見通し）を計算する
     * @param data 計画値・前回値・今回値を含む比較データ
     * @param summaryType 集計方法（"total": 合計比較 / "average": 平均比較）
     * @returns サマリーカードに表示する3値オブジェクト
     */
    const buildSummaryValuesForData = (data: ReviewComparisonData | undefined, summaryType: "total" | "average" | undefined) => {
      if (!data || !summaryType) {
        return null;
      }

      const isAvg = summaryType === "average";
      const actualVsPlan = isAvg
        ? calcActualVsPlanAvg(data.originalPlan, data.current, reviewBoundaryIndex)
        : calcActualVsPlan(data.originalPlan, data.current, reviewBoundaryIndex);
      const forecastAccuracy = isAvg
        ? calcForecastAccuracyAvg(data.previous, data.current, prevReviewIndex, reviewBoundaryIndex)
        : calcForecastAccuracy(data.previous, data.current, prevReviewIndex, reviewBoundaryIndex);
      const finalOutlook = isAvg
        ? calcFinalOutlookAvg(data.originalPlan, data.current)
        : calcFinalOutlook(data.originalPlan, data.current);
      return { actualVsPlan, forecastAccuracy, finalOutlook };
    };

    // 売上計画の対比のみ全体集計（合算）として残す。販売単価・販売数量は PartNo 別へ移動。
    const salesAmountSection = chartSectionsLocal.find((sec) => sec.id === "salesAmount");
    const salesAmountSectionJPY = chartSectionsJPY.find((sec) => sec.id === "salesAmount");
    const priceSectionTemplate = chartSectionsLocal.find((sec) => sec.id === "salesPrice");
    const qtySectionTemplate = chartSectionsLocal.find((sec) => sec.id === "salesQuantity");

    /**
     * 比較モード1本分のグラフ・スケジュールデータをまとめて生成する
     * sections（売上計画）・pricePartNoSections（PartNo別単価）・qtyPartNoSections（PartNo別数量）
     * の3種を含むオブジェクトを返す。
     * @param mode 比較モード（"previous": 前回値との比較 / "originalPlan": 申請時計画との比較）
     * @returns 比較モード1本分のデータオブジェクト
     */
    const buildSideForMode = (mode: CompareMode) => {
      const sections = salesAmountSection
        ? [{
          id: salesAmountSection.id,
          title: salesAmountSection.title,
          unit: salesAmountSection.unit,
          summaryType: salesAmountSection.summaryType,
          chartData: buildChartDataForMode(salesAmountSection.data, mode),
          summaryValues: {
            [localCurrencyName]: buildSummaryValuesForData(salesAmountSection.data, salesAmountSection.summaryType),
            [CurrencyNameJPY]: buildSummaryValuesForData(salesAmountSectionJPY?.data!, salesAmountSectionJPY?.summaryType),
          }
        }]
        : [];

      // PartNo 別 販売単価（ローカル通貨ベース）
      // 保存順は画面の PartNo SELECT（salesPartNos）と同じ順序にそろえる。
      // salesPartNos に存在しない PartNo は後ろに付け足す（保存データの欠落を防ぐ）。
      /**
       * dataByPartNo のキーを salesPartNos の表示順に並べた配列を返す
       * salesPartNos にない PartNo は末尾に追加する（保存データの欠落を防ぐ）
       * @param dataByPartNo PartNo をキーとする比較データの Record
       * @returns SELECT 順に並べ直した PartNo 配列
       */
      const orderPartNos = (dataByPartNo: Record<string, ReviewComparisonData>): string[] => {
        const keys = Object.keys(dataByPartNo);
        const orderedFromSelect = salesPartNos.filter((pn) => keys.includes(pn));
        const orderedRest = keys.filter((pn) => !salesPartNos.includes(pn));
        return [...orderedFromSelect, ...orderedRest];
      };

      const pricePartNoSections = priceSectionTemplate
        ? orderPartNos(priceDataByPartNo).map((partNo) => ({
          partNo,
          displayPartNo: displayPartNoMap[partNo] ?? null,
          unit: priceSectionTemplate.unit,
          summaryType: priceSectionTemplate.summaryType,
          chartData: buildChartDataForMode(priceDataByPartNo[partNo], mode),
          summaryValues: {
            [localCurrencyName]: buildSummaryValuesForData(priceDataByPartNo[partNo], priceSectionTemplate.summaryType),
            [CurrencyNameJPY]: buildSummaryValuesForData(priceDataByPartNoJPY[partNo], priceSectionTemplate.summaryType),
          }
        }))
        : [];

      // PartNo 別 販売数量（通貨非依存）
      const qtyPartNoSections = qtySectionTemplate
        ? orderPartNos(qtyDataByPartNo).map((partNo) => ({
          partNo,
          displayPartNo: displayPartNoMap[partNo] ?? null,
          unit: qtySectionTemplate.unit,
          summaryType: qtySectionTemplate.summaryType,
          chartData: buildChartDataForMode(qtyDataByPartNo[partNo], mode),
          summaryValues: buildSummaryValuesForData(qtyDataByPartNo[partNo], qtySectionTemplate.summaryType),
        }))
        : [];

      return {
        sections,
        pricePartNoSections,
        qtyPartNoSections,
        shareData: { plan: sharePlan, previous: sharePrevious, review: shareReview },
        scheduleData: {
          plan: planScheduleItems,
          previous: prevScheduleItems,
          review: reviewScheduleItems,
          planFacility: planFacilityItems,
          prevFacility: prevFacilityItems,
          reviewFacility: reviewFacilityItems,
          planProjectPeriod,
        },
      };
    };

    return {
      monthLabels,
      boundary: reviewBoundaryIndex,
      prevBoundary: prevReviewIndex,
      localCurrencyName,
      previousMode: buildSideForMode("previous"),
      originalPlanMode: buildSideForMode("originalPlan"),
    };
  };

  // ========== Excel出力 ==========
  const handleExcelExport = () => {
    if (chartSections.length === 0) {
      toast({ title: TITLE, description: t('common.message.excelExportNoData'), status: 'warning', duration: 5000, isClosable: true });
      return;
    }

    const workbook = XLSX.utils.book_new();

    // 出力日時シート
    const now = new Date(Date.now() + 9 * 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const dateStr = `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())} ` +
      `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`;
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([[dateStr]]), t('features.InvestmentScreening.Review.ReviewSales.excel.sheetName.exportDateTime'));

    // 売上計画シート（全体合算）
    const salesAmountSection = chartSections.find(s => s.id === 'salesAmount');
    if (salesAmountSection) {
      const unit = salesAmountSection.unit;
      const data: (string | number)[][] = [
        ['', ...monthLabels],
        [`${t('common.label.originalPlan')} (${unit})`, ...salesAmountSection.data.originalPlan],
        [`${t('common.label.previousValue')} (${unit})`, ...salesAmountSection.data.previous],
        [`${t('common.label.currentValue')} (${unit})`, ...salesAmountSection.data.current],
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = getAutoColWidths(data);
      XLSX.utils.book_append_sheet(workbook, ws, t('features.InvestmentScreening.Review.ReviewSales.excel.sheetName.salesAmount'));
    }

    // 販売単価シート（全PartNoを縦積み、1シート）
    const priceData = currencyName === CurrencyNameJPY ? priceDataByPartNoJPY : priceDataByPartNo;
    const priceUnit = currencyName;
    if (salesPartNos.length > 0) {
      const data: (string | number)[][] = [];
      for (let i = 0; i < salesPartNos.length; i++) {
        const pn = salesPartNos[i];
        const d = priceData[pn];
        if (!d) continue;
        if (data.length > 0) data.push([]);

        if (salesPartNos.length >= 2) {
          data.push([`${t('common.label.partNo')} ${displayPartNoMap[pn] || pn}`]);
        }

        data.push(['', ...monthLabels]);
        data.push([`${t('common.label.originalPlan')} (${priceUnit})`, ...d.originalPlan]);
        data.push([`${t('common.label.previousValue')} (${priceUnit})`, ...d.previous]);
        data.push([`${t('common.label.currentValue')} (${priceUnit})`, ...d.current]);
      }
      if (data.length > 0) {
        const ws = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = getAutoColWidths(data);
        XLSX.utils.book_append_sheet(workbook, ws, t('features.InvestmentScreening.Review.ReviewSales.excel.sheetName.salesPrice'));
      }
    }

    // 販売数量シート（全体合算 + PartNo別縦積み、1シート）
    const salesQtySection = chartSections.find(s => s.id === 'salesQuantity');
    if (salesQtySection) {
      const data: (string | number)[][] = [];
      // 全体合算ブロック（複数PartNoの場合はラベルを付ける）
      if (salesPartNos.length >= 2) data.push([t('features.InvestmentScreening.Review.ReviewSales.excel.overallTotal')]);
      data.push(['', ...monthLabels]);
      data.push([`${t('common.label.originalPlan')} (${t('common.label.unitPcs')})`, ...salesQtySection.data.originalPlan]);
      data.push([`${t('common.label.previousValue')} (${t('common.label.unitPcs')})`, ...salesQtySection.data.previous]);
      data.push([`${t('common.label.currentValue')} (${t('common.label.unitPcs')})`, ...salesQtySection.data.current]);
      // PartNo別ブロック（PartNoが2以上の場合のみ追加）
      if (salesPartNos.length >= 2) {
        for (const pn of salesPartNos) {
          const d = qtyDataByPartNo[pn];
          if (!d) continue;
          data.push([]);
          data.push([`${t('common.label.partNo')} ${displayPartNoMap[pn] || pn}`]);
          data.push(['', ...monthLabels]);
          data.push([`${t('common.label.originalPlan')} (${t('common.label.unitPcs')})`, ...d.originalPlan]);
          data.push([`${t('common.label.previousValue')} (${t('common.label.unitPcs')})`, ...d.previous]);
          data.push([`${t('common.label.currentValue')} (${t('common.label.unitPcs')})`, ...d.current]);
        }
      }
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = getAutoColWidths(data);
      XLSX.utils.book_append_sheet(workbook, ws, t('features.InvestmentScreening.Review.ReviewSales.excel.sheetName.salesQuantity'));
    }

    const timestamp = dateStr.replace(/[-: ]/g, '').slice(0, 14);
    XLSX.writeFile(workbook, `${t('features.InvestmentScreening.Review.ReviewSales.excel.fileNamePrefix')}_${projectId}_${timestamp}.xlsx`);
  };

  // ========== 確認ボタン ==========
  const handleModalOpen: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.currentTarget.blur();
    if (!reviewDateProp) {
      toast({ title: TITLE, description: t("common.message.reviewDateNotSet"), status: 'warning', duration: 5000, isClosable: true });
      return;
    }
    if (!hasReviewData) {
      toast({ title: TITLE, description: t("features.InvestmentScreening.Review.ReviewSales.schedule.graphNotDisplayedSales"), status: 'warning', duration: 5000, isClosable: true });
      return;
    }
    const { name } = event.target as HTMLButtonElement;
    setConfirmSource(name);
    onOpenConfirmModal();
  };

  /**
   * 「確認」ボタン押下
   * @param e イベント情報
   * @returns 
   */
  const handleConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    setIsSaving(true);
    try {
      const salesReview = JSON.stringify({ textValues, comments, supplementaryValues });
      const salesChartData = JSON.stringify(buildSalesChartData());
      const saveResp = await (WebAPI as any).request({
        query: `
          mutation putReview($input: PutReviewInput!) {
            putReview(input: $input) { status message }
          }
        `,
        variables: { input: { projectId, reviewDate: reviewDateProp, salesReview, salesChartData } }
      });
      const saveResult = saveResp?.data?.putReview;
      if (saveResult?.status !== 'success') {
        toast({ title: TITLE, description: saveResult?.message ? t(saveResult.message) : t("common.message.saveFailed"), status: 'error', duration: 5000, isClosable: true });
        return;
      }
      await updateConfirm(WebAPI, projectId, WorkflowId.ReviewSales, employeeId, confirmSource, "true", reviewDateProp);
      const data = await getConfirmor(WebAPI, projectId, reviewDateProp);
      setConfirmor(data);
      toast({ title: TITLE, description: t("common.message.inputSaved"), status: 'success', duration: 3000, isClosable: true });
      onCloseConfirmModal();
    } catch { /* スルー */ } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      position="fixed"
      top="10vh"
      ml="2vw"
      mr="2vw"
      left="250px"
      width="calc(96vw - 250px)"
      height="85vh"
      p={6}
      overflowY="auto"
      bg="white"
      textAlign="left"
    >


      {/* 操作パネル（sticky固定） */}
      <Box
        position="sticky"
        top={-6}
        zIndex={10}
        bg="white"
        pt={6}
        pb={3}
        mb={4}
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <HStack spacing={6} align="center" wrap="wrap">
          <ButtonGroup size="sm" isAttached>
            <Button
              colorScheme={compareMode === "previous" ? "blue" : "gray"}
              variant={compareMode === "previous" ? "solid" : "outline"}
              onClick={() => setCompareMode("previous")}
            >
              {t("common.button.comparePreviousValue")}
            </Button>
            <Button
              colorScheme={compareMode === "originalPlan" ? "green" : "gray"}
              variant={compareMode === "originalPlan" ? "solid" : "outline"}
              onClick={() => setCompareMode("originalPlan")}
            >
              {t("common.button.compareOriginalPlan")}
            </Button>
          </ButtonGroup>
          <HStack spacing={2} align="center">
            <Text fontSize="xs" color="gray.500" flexShrink={0}>{t("common.label.displayPeriod")}</Text>
            <Select
              size="sm"
              w="260px"
              value={periodStart}
              onChange={(e) => {
                const newStart = Number(e.target.value);
                if (newStart < periodEnd) setPeriodStart(newStart);
              }}
            >
              {milestones
                .filter(m => m.idx < periodEnd)
                .map((m, i) => (
                  <option key={`s-${i}-${m.idx}`} value={m.idx}>{m.label}</option>
                ))}
            </Select>
            <Text fontSize="sm" color="gray.500">{t("common.label.symbolTo")}</Text>
            <Select
              size="sm"
              w="260px"
              value={periodEnd}
              onChange={(e) => {
                const newEnd = Number(e.target.value);
                if (newEnd > periodStart) setPeriodEnd(newEnd);
              }}
            >
              {milestones
                .filter(m => m.idx > periodStart)
                .map((m, i) => (
                  <option key={`e-${i}-${m.idx}`} value={m.idx}>{m.label}</option>
                ))}
            </Select>
            <Button
              size="xs"
              variant="outline"
              colorScheme="gray"
              onClick={() => { setPeriodStart(0); setPeriodEnd(monthLabels.length); }}
            >
              {t("common.button.allPeriods")}
            </Button>
          </HStack>
          <HStack spacing={2} align="center">
            <Text fontSize="xs" color="gray.500" flexShrink={0}>{t("common.label.displayCurrency")}</Text>
            <HelpIcon size="14" placement="top">
              <Text>{t("features.InvestmentScreening.Review.common.displayCurrencyRateHelp")}</Text>
            </HelpIcon>
            <Select
              size="sm"
              w="120px"
              value={currencyName}
              onChange={(e) => {
                const newCurr = e.target.value;
                setCurrencyName(newCurr);
                if (newCurr === CurrencyNameJPY && chartSectionsJPY.length > 0) {
                  setChartSections(chartSectionsJPY);
                } else {
                  setChartSections(chartSectionsLocal);
                }
              }}
            >
              <option value={localCurrencyName}>{localCurrencyName}</option>
              {localCurrencyName !== CurrencyNameJPY && (
                <option value={CurrencyNameJPY}>{CurrencyNameJPY}</option>
              )}
            </Select>
          </HStack>
        </HStack>
      </Box>

      {/* グラフセクション（簡易モードでは非表示） */}
      {!isSimpleMode && chartSections.map((section) => {
        const isPriceSection = section.id === "salesPrice";
        const isQtySection = section.id === "salesQuantity";
        const showPartNoSelector = salesPartNos.length > 1 && (isPriceSection || isQtySection);

        let displaySection = section;
        if (isPriceSection && selectedPartNoPriceGraph !== "") {
          const pnPriceData = currencyName === CurrencyNameJPY
            ? priceDataByPartNoJPY[selectedPartNoPriceGraph]
            : priceDataByPartNo[selectedPartNoPriceGraph];
          if (pnPriceData) displaySection = { ...section, data: pnPriceData };
        } else if (isQtySection && selectedPartNoQtyGraph !== "all" && qtyDataByPartNo[selectedPartNoQtyGraph]) {
          displaySection = { ...section, data: qtyDataByPartNo[selectedPartNoQtyGraph] };
        }

        const isTotal = displaySection.summaryType === "total";
        // データが全て0かどうか（振り返りデータ未入力の判定）
        const hasCurrentData = displaySection.data.current.some(v => v !== 0);

        const s1 = isTotal
          ? calcActualVsPlan(displaySection.data.originalPlan, displaySection.data.current, reviewBoundaryIndex)
          : calcActualVsPlanAvg(displaySection.data.originalPlan, displaySection.data.current, reviewBoundaryIndex);
        const s2 = isTotal
          ? calcForecastAccuracy(displaySection.data.previous, displaySection.data.current, prevReviewIndex, reviewBoundaryIndex)
          : calcForecastAccuracyAvg(displaySection.data.previous, displaySection.data.current, prevReviewIndex, reviewBoundaryIndex);
        const s3 = isTotal
          ? calcFinalOutlook(displaySection.data.originalPlan, displaySection.data.current)
          : calcFinalOutlookAvg(displaySection.data.originalPlan, displaySection.data.current);
        // 金額項目は通貨に応じたフォーマット、数量項目はカンマ区切り
        const isMoney = section.unit !== "pcs";
        const fmtVal = (v: number) => {
          if (isMoney) {
            // 末尾の0を除去して表示（14.380 → 14.38）
            const dp = hasDecimal(currencyName) ? 4 : 0;
            const formatted = formatNumber(v, currencyName, dp);
            if (hasDecimal(currencyName) && formatted.includes('.')) {
              return formatted.replace(/0+$/, '').replace(/\.$/, '');
            }
            return formatted;
          }
          return isTotal ? Math.round(v).toLocaleString() : v.toFixed(1);
        };

        return (
          <Box key={section.id} mb={8} p={4} border="1px" borderColor="gray.200" borderRadius="md">
            <HStack mb={3} justify="space-between" align="center">
              <HStack align="baseline" spacing={2}>
                <Text fontSize="md" fontWeight="bold">{section.title}</Text>
                <Text fontSize="sm" color="gray.500">unit: {section.unit}</Text>
              </HStack>
              {showPartNoSelector && (
                <HStack spacing={2}>
                  <Text fontSize="xs" color="gray.500">{t("common.label.partNo")}</Text>
                  <Select
                    size="sm"
                    w="200px"
                    value={isPriceSection ? selectedPartNoPriceGraph : selectedPartNoQtyGraph}
                    onChange={(e) => {
                      if (isPriceSection) setSelectedPartNoPriceGraph(e.target.value);
                      else setSelectedPartNoQtyGraph(e.target.value);
                    }}
                  >
                    {!isPriceSection && <option value="all">{t("common.label.all")}</option>}
                    {salesPartNos.map(pn => (
                      <option key={pn} value={pn}>{displayPartNoMap[pn] || pn}</option>
                    ))}
                  </Select>
                </HStack>
              )}
            </HStack>

            {hasCurrentData ? (
              <>
                {/* サマリ: 3カード形式 */}
                <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                  <SimpleGrid columns={3} spacing={3}>
                    <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {t("features.InvestmentScreening.Review.common.summaryActualVsPlan", {
                          from: formatYearMonthByLocale(monthLabels[0]),
                          to: formatYearMonthByLocale(monthLabels[Math.min(reviewBoundaryIndex, monthLabels.length) - 1]),
                        })}
                      </Text>
                      <HStack spacing={2} align="baseline">
                        <Text fontSize="sm" color="gray.500">{t("common.label.plan")} {fmtVal(s1.planValue)}</Text>
                        <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                        <Text fontSize="sm" fontWeight="bold">{t("common.label.actuals")} {fmtVal(s1.actualValue)}</Text>
                      </HStack>
                      <Text fontSize="lg" fontWeight="bold" mt={1} color={s1.rate < -10 ? "red.600" : s1.rate < 0 ? "orange.500" : "green.600"}>
                        {s1.rate >= 0 ? "+" : ""}{s1.rate.toFixed(1)}%
                      </Text>
                    </Box>
                    <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {t("features.InvestmentScreening.Review.common.summaryAdjustedPlan", {
                          from: formatYearMonthByLocale(monthLabels[0]),
                          to: formatYearMonthByLocale(monthLabels[monthLabels.length - 1]),
                        })}
                      </Text>
                      <HStack spacing={2} align="baseline">
                        <Text fontSize="sm" color="gray.500">{t("common.label.plan")} {fmtVal(s3.planTotal)}</Text>
                        <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                        <Text fontSize="sm" fontWeight="bold">{t("common.label.outlook")} {fmtVal(s3.outlookTotal)}</Text>
                      </HStack>
                      <Text fontSize="lg" fontWeight="bold" mt={1} color={s3.rate < -10 ? "red.600" : s3.rate < 0 ? "orange.500" : "green.600"}>
                        {s3.rate >= 0 ? "+" : ""}{s3.rate.toFixed(1)}%
                      </Text>
                    </Box>
                    <Box p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {t("features.InvestmentScreening.Review.common.summaryForecastAccuracy", {
                          from: formatYearMonthByLocale(monthLabels[Math.min(prevReviewIndex, monthLabels.length - 1)]),
                          to: formatYearMonthByLocale(monthLabels[Math.min(reviewBoundaryIndex, monthLabels.length) - 1]),
                        })}
                      </Text>
                      <HStack spacing={2} align="baseline">
                        <Text fontSize="sm" color="gray.500">{t("common.label.previousEstimate")} {fmtVal(s2.prevEstimate)}</Text>
                        <Text fontSize="sm" color="gray.400">{t("common.label.arrowTo")}</Text>
                        <Text fontSize="sm" fontWeight="bold">{t("common.label.actuals")} {fmtVal(s2.currActual)}</Text>
                      </HStack>
                      <Text fontSize="lg" fontWeight="bold" mt={1} color={s2.rate < -10 ? "red.600" : s2.rate < 0 ? "orange.500" : "green.600"}>
                        {s2.rate >= 0 ? "+" : ""}{s2.rate.toFixed(1)}%
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Box h="280px" mb={3}>
                  <Bar
                    data={createChartData(displaySection, periodStart, periodEnd)}
                    options={createChartOptions(displaySection, periodStart, periodEnd) as any}
                  />
                </Box>
              </>
            ) : (
              <Box p={6} bg="gray.50" borderRadius="md" textAlign="center">
                <Text fontSize="sm" color="gray.500">{t("common.message.reviewDataNotRegistered")}</Text>
              </Box>
            )}

            <Textarea
              size="sm"
              placeholder={t("common.placeholder.diffComment")}
              rows={2}
              maxLength={500}
              mt={3}
              value={comments[section.id] || ""}
              onChange={(e) => setComments({ ...comments, [section.id]: e.target.value })}
            />
          </Box>
        );
      })}

      {/* ④ シェア率比較 */}
      <Box mb={8} p={4} border="1px" borderColor="gray.200" borderRadius="md">
        <Text fontSize="md" fontWeight="bold" mb={3}>{t("features.InvestmentScreening.Review.ReviewSales.section.share")}</Text>
        {sharePlan || shareReview || sharePrevious ? (() => {
          const compareShare = compareMode === "previous"
            ? (sharePrevious ?? sharePlan)
            : sharePlan;
          const compareLabel = compareMode === "previous"
            ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.applicationTime")) // 申請時に統一
            : t("common.label.applicationTime");
          const headerBg = compareMode === "previous" ? "blue.600" : "green.600";
          return (
            <Table variant="simple" size="sm" border="1px" borderColor="gray.200">
              <Thead>
                <Tr bg={headerBg}>
                  <Th color="white" w="25%">{t("common.label.item")}</Th>
                  <Th color="white" w="37.5%">{compareLabel}</Th>
                  <Th color="white" w="37.5%">{t("common.label.currentColumn")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="semibold" fontSize="sm">{t("common.label.ourShare")}</Td>
                  <Td fontSize="sm">
                    {compareShare?.sharePercentage != null
                      ? `${compareShare.sharePercentage}%`
                      : <Text color="gray.400">—</Text>}
                  </Td>
                  <Td fontSize="sm">
                    {shareReview?.sharePercentage != null ? (
                      <Text
                        fontWeight="bold"
                        color={
                          compareShare?.sharePercentage != null && shareReview.sharePercentage > compareShare.sharePercentage
                            ? "green.600"
                            : compareShare?.sharePercentage != null && shareReview.sharePercentage < compareShare.sharePercentage
                              ? "red.600"
                              : "gray.700"
                        }
                      >
                        {shareReview.sharePercentage}%
                        {compareShare?.sharePercentage != null && (
                          <Text as="span" fontWeight="normal" color="gray.500" ml={2}>
                            ({shareReview.sharePercentage - compareShare.sharePercentage >= 0 ? "+" : ""}{shareReview.sharePercentage - compareShare.sharePercentage}pt)
                          </Text>
                        )}
                      </Text>
                    ) : <Text color="gray.400">—</Text>}
                  </Td>
                </Tr>
                <Tr _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="semibold" fontSize="sm">{t("common.label.ourCompetitorShare")}</Td>
                  <Td fontSize="sm" whiteSpace="pre-wrap">
                    {compareShare?.shareOfCompetitorAndCompany || <Text color="gray.400">—</Text>}
                  </Td>
                  <Td fontSize="sm" whiteSpace="pre-wrap">
                    {shareReview?.shareOfCompetitorAndCompany || <Text color="gray.400">—</Text>}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          );
        })() : (
          <Box p={6} bg="gray.50" borderRadius="md" textAlign="center">
            <Text fontSize="sm" color="gray.500">{t("features.InvestmentScreening.Review.common.registerShareInCustomerTerms")}</Text>
          </Box>
        )}
        <Textarea
          size="sm"
          placeholder={t("features.InvestmentScreening.Review.common.shareCommentPlaceholder")}
          rows={2}
          maxLength={500}
          mt={3}
          value={comments["marketShare"] || ""}
          onChange={(e) => setComments({ ...comments, marketShare: e.target.value })}
        />
      </Box>

      {/* ⑤ 販売期間（スケジュール対比） */}
      <Box mb={8} p={4} border="1px" borderColor="gray.200" borderRadius="md">
        <Text fontSize="md" fontWeight="bold" mb={3}>{t("features.InvestmentScreening.Review.ReviewSales.section.salesPeriod")}</Text>

        {/* プロジェクト期間（申請時） */}
        {planProjectPeriod && (
          <Box mb={3} px={3} py={2} bg="blue.50" borderRadius="sm" fontSize="sm">
            <Text>{t("features.InvestmentScreening.Review.common.projectPeriodApplication", {
              start: formatMonthStr(planProjectPeriod.start),
              end: formatMonthStr(planProjectPeriod.end),
            })}</Text>
          </Box>
        )}

        {(() => {
          // 比較モードに応じて基準となるスケジュール一覧を決定
          const baseScheduleItems = compareMode === "previous"
            ? (prevScheduleItems.length > 0 ? prevScheduleItems : planScheduleItems)
            : planScheduleItems;
          const scheduleCompareLabel = compareMode === "previous"
            ? (prevReviewDateState ? t("common.label.previousValue") : t("common.label.applicationPeriod")) // 「申請時 / 申請期間」の文言に統一
            : t("common.label.applicationPeriod");
          const scheduleHeaderBg = compareMode === "previous" ? "blue.600" : "green.600";

          // `item.id`(ID（UUID）) を変数展開
          const baseWithKey = baseScheduleItems.map((item) => ({
            ...item,
            matchKey: String(item.id)
          }));

          const reviewWithKey = reviewScheduleItems.map((item) => ({
            ...item,
            matchKey: String(item.id)
          }));

          // 1. 基準と今回の双方から、すべての matchKey を重複なく抽出
          const allMatchKeys = Array.from(
            new Set([
              ...baseWithKey.map(item => item.matchKey),
              ...reviewWithKey.map(item => item.matchKey)
            ])
          );

          // 2. 入力漏れ（未登録）警告メッセージを表示するかどうかの判定
          const hasUnregisteredActual = baseWithKey.some(base => {
            const review = reviewWithKey.find(r => r.matchKey === base.matchKey);
            return review && !review.date;
          });

          return allMatchKeys.length > 0 ? (
            <>
              <Table variant="simple" size="sm" border="1px" borderColor="gray.200" mb={2}>
                <Thead>
                  <Tr bg={scheduleHeaderBg}>
                    <Th color="white" w="30%">{t("common.label.milestone")}</Th>
                    <Th color="white" w="25%">{scheduleCompareLabel}</Th>
                    <Th color="white" w="25%">{t("common.label.currentActual")}</Th>
                    <Th color="white" w="20%">{t("common.label.differenceShort")}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {allMatchKeys.map((key) => {
                    const base = baseWithKey.find(b => b.matchKey === key);
                    const review = reviewWithKey.find(r => r.matchKey === key);

                    // 表示用マイルストーン名
                    const rawName = base ? base.name : (review ? review.name : "");

                    // 両方の日付データが揃っている場合のみ差分日数を計算
                    const diffDays = (base && base.date && review && review.date)
                      ? calcDiffDays(base.date, review.date)
                      : null;

                    return (
                      <Tr key={key} _hover={{ bg: "gray.50" }}>
                        <Td fontWeight="semibold" fontSize="sm">{rawName}</Td>

                        {/* 前回値 / 申請時 */}
                        <Td fontSize="sm">
                          {base ? formatDateStr(base.date) : <Text color="gray.400">—</Text>}
                        </Td>

                        {/* 今回実績 */}
                        <Td fontSize="sm">
                          {review ? formatDateStr(review.date) : <Text color="gray.400">—</Text>}
                        </Td>

                        {/* 差分・ステータス */}
                        <Td fontSize="sm">
                          {(() => {
                            // スケジュール追加
                            if (!base && review) {
                              return (
                                <Text color="green.600" fontWeight="bold">
                                  {t("features.InvestmentScreening.Review.ReviewSales.schedule.newlyAdded")}
                                </Text>
                              );
                            }
                            // スケジュール削除
                            if (base && !review) {
                              return (
                                <Text color="red.600" fontWeight="bold">
                                  {t("features.InvestmentScreening.Review.ReviewSales.schedule.scheduleDeleted")}
                                </Text>
                              );
                            }
                            // 通常の日付変更による差分表示
                            if (diffDays !== null) {
                              return (
                                <Text
                                  fontWeight="bold"
                                  color={diffDays > 14 ? "red.600" : diffDays < -14 ? "green.600" : "gray.700"}
                                >
                                  {formatDiff(diffDays)}
                                </Text>
                              );
                            }
                            return <Text color="gray.400">—</Text>;
                          })()}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>

              {/* 入力漏れの項目があるとき警告表示 */}
              {hasUnregisteredActual && (
                <Text fontSize="xs" color="orange.500" mt={1}>
                  {t("features.InvestmentScreening.Review.common.actualScheduleNotRegistered")}
                </Text>
              )}
            </>
          ) : (
            <Box p={4} bg="gray.50" borderRadius="md" textAlign="center" mb={3}>
              <Text fontSize="sm" color="gray.500">
                {t("features.InvestmentScreening.Review.common.applicationScheduleNotRegistered")}
              </Text>
            </Box>
          );
        })()}

        {/* 設備投資スケジュール（遅延原因参照） */}
        {(planFacilityItems.length > 0 || prevFacilityItems.length > 0) && (
          <Accordion allowToggle mt={3}>
            <AccordionItem border="1px" borderColor="gray.200" borderRadius="md">
              <AccordionButton px={3} py={2} _expanded={{ bg: "gray.50" }}>
                <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold" color="gray.600">
                  {t("features.InvestmentScreening.Review.ReviewSales.section.equipmentSchedule")}
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel p={0}>
                {(() => {
                  const baseFacilityItems = compareMode === "previous"
                    ? (prevFacilityItems.length > 0 ? prevFacilityItems : planFacilityItems)
                    : planFacilityItems;
                  const facilityCompareLabel = compareMode === "previous"
                    ? (prevReviewDateState ? t("common.label.previousReviewPeriod") : t("common.label.applicationPeriodInitial"))
                    : t("common.label.planPeriod");
                  const facilityHeaderBg = compareMode === "previous" ? "gray.500" : "gray.600";

                  // `item.id`(ID（UUID）) を変数展開
                  const baseFacilityWithKey = baseFacilityItems.map((item) => ({
                    ...item,
                    matchKey: String(item.id)
                  }));

                  const reviewFacilityWithKey = reviewFacilityItems.map((item) => ({
                    ...item,
                    matchKey: String(item.id)
                  }));

                  // 1. 基準と今回の双方から、すべての設備 matchKey を重複なく抽出
                  const allFacilityMatchKeys = Array.from(
                    new Set([
                      ...baseFacilityWithKey.map(item => item.matchKey),
                      ...reviewFacilityWithKey.map(item => item.matchKey)
                    ])
                  );

                  // 2. 入力漏れ（未登録）を表示するかどうかの判定
                  const hasUnregisteredFacility = baseFacilityWithKey.some(base => {
                    const review = reviewFacilityWithKey.find(r => r.matchKey === base.matchKey);
                    return review && (!review.startDate || !review.endDate);
                  });

                  return (
                    <>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr bg={facilityHeaderBg}>
                            <Th color="white" w="28%">{t("common.label.equipmentName")}</Th>
                            <Th color="white" w="30%">{facilityCompareLabel}</Th>
                            <Th color="white" w="30%">{t("common.label.currentActualPeriod")}</Th>
                            <Th color="white" w="12%">{t("common.label.differenceShort")}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {allFacilityMatchKeys.map((key) => {
                            const base = baseFacilityWithKey.find(b => b.matchKey === key);
                            const review = reviewFacilityWithKey.find(r => r.matchKey === key);

                            const rawFacilityName = base ? base.name : (review ? review.name : "");

                            const startDiff = (base && base.startDate && review && review.startDate) ? calcDiffDays(base.startDate, review.startDate) : null;
                            const endDiff = (base && base.endDate && review && review.endDate) ? calcDiffDays(base.endDate, review.endDate) : null;

                            return (
                              <Tr key={key} _hover={{ bg: "gray.50" }}>
                                <Td fontWeight="semibold" fontSize="sm">{rawFacilityName}</Td>

                                {/* 基準期間（前回 or 申請時） */}
                                <Td fontSize="xs" color="gray.700">
                                  {base ? (
                                    <>{formatDateStr(base.startDate)}<Text as="span" color="gray.400"> {t("common.label.symbolTo")} </Text>{formatDateStr(base.endDate)}</>
                                  ) : (
                                    <Text color="gray.400">—</Text>
                                  )}
                                </Td>

                                {/* 今回実績期間 */}
                                <Td fontSize="xs">
                                  {review ? (
                                    <>{formatDateStr(review.startDate)}<Text as="span" color="gray.400"> {t("common.label.symbolTo")} </Text>{formatDateStr(review.endDate)}</>
                                  ) : (
                                    <Text color="gray.400">—</Text>
                                  )}
                                </Td>

                                {/* 差分・ステータス */}
                                <Td fontSize="xs">
                                  {(() => {
                                    // スケジュール追加
                                    if (!base && review) {
                                      return (
                                        <Text color="green.600" fontWeight="bold">
                                          {t("features.InvestmentScreening.Review.ReviewSales.schedule.newlyAdded")}
                                        </Text>
                                      );
                                    }
                                    // スケジュール削除
                                    if (base && !review) {
                                      return (
                                        <Text color="red.600" fontWeight="bold">
                                          {t("features.InvestmentScreening.Review.ReviewSales.schedule.scheduleDeleted")}
                                        </Text>
                                      );
                                    }
                                    // 通常の開始・終了日付変更による差分表示
                                    if (startDiff !== null && endDiff !== null) {
                                      return (
                                        <VStack align="start" spacing={0}>
                                          <Text
                                            fontWeight="bold"
                                            color={startDiff > 14 ? "red.600" : startDiff < -14 ? "green.600" : "gray.700"}
                                          >
                                            {t("common.label.start")} {formatDiff(startDiff)}
                                          </Text>
                                          <Text
                                            fontWeight="bold"
                                            color={endDiff > 14 ? "red.600" : endDiff < -14 ? "green.600" : "gray.700"}
                                          >
                                            {t("common.label.end")} {formatDiff(endDiff)}
                                          </Text>
                                        </VStack>
                                      );
                                    }
                                    return <Text color="gray.400">—</Text>;
                                  })()}
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>

                      {/* 日付が空の未登録データがある場合メッセージ */}
                      {hasUnregisteredFacility && (
                        <Text fontSize="xs" color="orange.500" px={3} py={2}>
                          {t("features.InvestmentScreening.Review.common.equipmentScheduleHint")}
                        </Text>
                      )}
                    </>
                  );
                })()}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}

        <Textarea
          size="sm"
          placeholder={t("features.InvestmentScreening.Review.common.scheduleChangeReason")}
          rows={2}
          maxLength={500}
          mt={3}
          value={textValues["sales_schedule"] || ""}
          onChange={(e) => setTextValues({ ...textValues, sales_schedule: e.target.value })}
        />
      </Box>

      {/* テキスト入力項目 */}
      <Box mb={6}>
        <Text fontSize="md" fontWeight="bold" mb={3}>{t("features.InvestmentScreening.Review.ReviewSales.section.textItems")}</Text>
        <Table variant="simple" size="sm" border="1px" borderColor="gray.200">
          <Thead>
            <Tr bg={compareMode === "previous" ? "blue.600" : "green.600"}>
              <Th w="30%" color="white">{t("common.label.item")}</Th>
              <Th w="70%" color="white">{t("common.label.content")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {TEXT_ITEM_IDS.map((id) => (
              <Tr key={id} _hover={{ bg: "gray.50" }}>
                <Td fontWeight="semibold" fontSize="sm">
                  {t(`features.InvestmentScreening.Review.ReviewSales.textItems.${id}.label`)}
                </Td>
                <Td>
                  <Textarea
                    size="sm"
                    placeholder={t(`features.InvestmentScreening.Review.ReviewSales.textItems.${id}.placeholder`)}
                    rows={2}
                    maxLength={500}
                    value={textValues[id] || ""}
                    onChange={(e) => setTextValues({ ...textValues, [id]: e.target.value })}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* 補足視点 */}
      <Box mb={6} p={4} bg="gray.50" borderRadius="md">
        <Text fontSize="md" fontWeight="bold" mb={3}>{t("features.InvestmentScreening.Review.ReviewSales.section.supplementaryViews")}</Text>
        <VStack align="start" spacing={3}>
          {SUPPLEMENTARY_ITEM_IDS.map((id) => (
            <HStack key={id} w="100%" align="start">
              <Text fontSize="sm" w="200px" flexShrink={0} pt={1}>
                {t(`features.InvestmentScreening.Review.ReviewSales.supplementaryItems.${id}.label`)}:
              </Text>
              <Textarea
                size="sm"
                placeholder={t(`features.InvestmentScreening.Review.ReviewSales.supplementaryItems.${id}.placeholder`)}
                rows={2}
                maxLength={500}
                value={supplementaryValues[id] || ""}
                onChange={(e) => setSupplementaryValues({ ...supplementaryValues, [id]: e.target.value })}
              />
            </HStack>
          ))}
        </VStack>
      </Box>

      {/* Excel出力・確認ボタン */}
      <Flex justify="space-between" mt="20px" mb={3}>
        <Box>
          <Button
            colorScheme="green"
            onClick={handleExcelExport}
            isDisabled={chartSections.length === 0}
          >
            {t('common.button.excelExport')}
          </Button>
        </Box>
        <Flex>
          <Box>
            <Button
              colorScheme="blue"
              name="companyDivision"
              onClick={handleModalOpen}
              isDisabled={!pagePermissions?.eabledCompanyDivision}
              ref={refs["companyDivision"]}
            >
              {t("common.constants.divisionConfirm.company")}
            </Button>
            <ConfirmorList confirmorInfo={names["companyDivision"]} width={buttonWidths["companyDivision"]} />
          </Box>
          <Box>
            <Button
              ml="10px"
              colorScheme="blue"
              name="salesDivision"
              onClick={handleModalOpen}
              isDisabled={!pagePermissions?.eabledSalesDivision}
              ref={refs["salesDivision"]}
            >
              {t("common.constants.divisionConfirm.sales")}
            </Button>
            <ConfirmorList confirmorInfo={names["salesDivision"]} width={buttonWidths["salesDivision"]} />
          </Box>
        </Flex>
      </Flex>

      {/* 確認モーダル */}
      <Modal isOpen={isOpenConfirmModal} onClose={onCloseConfirmModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("common.button.save")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{t("common.message.confirmSave")}</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} isDisabled={isSaving} isLoading={isSaving} onClick={handleConfirm}>
              {t("common.button.save")}
            </Button>
            <Button variant="ghost" onClick={onCloseConfirmModal}>{t("common.button.cancel")}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ReviewSales;
